import { analysisSummaryStats } from '../utils/analysisSummaryStats';
import { TRAINING_ZONE_COLORS, TRAINING_ZONE_GRADIENTS } from '../utils/trainingZones';
import { NmLoader } from '../components/NmLoader';
import { ActionButton, ActionIconButton, ActionToggleGroup } from '../components/ui/ActionButton';
import '../styles/workout-detail-system.css';
import { WorkoutPhysiologyPanels } from '../components/WorkoutPhysiologyPanels';
import { calendarWorkoutState, localCalendarDay } from '../utils/calendarWorkoutState';
import { displayDigits } from '../utils/displayDigits';
import { ResilientMapTiles } from '../components/ResilientMapTiles';
import { WorkoutShareModal } from '../components/WorkoutShareModal';
import { buildStreamMetricPath } from '../utils/workoutStream';
import { selectionDecoupling } from '../utils/selectionDecoupling';
import { AthleteZoneBars as ZoneBars } from '../components/charts/AthleteZoneBars';
import { athleteZoneDistribution } from '../utils/athleteZoneDistribution';
import { isRunningWorkout, runningDisplayStream, paceFromKmh, formatRunningPace } from '../utils/runningWorkoutMetrics';
import { runningSelectionMetrics } from '../utils/runningSelectionMetrics';
import { RunningMetricsPanel } from '../components/RunningMetricsPanel';
import { WorkoutTypeIcon } from '../components/WorkoutTypeIcon';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { MapContainer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { workouts, analyze } from '../services/api';
import type { WorkoutDetail } from '../services/api';
import { workoutStressLabel } from '../utils/workoutStressLabel';
import {
  PdcCurveChart,
  normalizePeakCurve,
  defaultCompareRange,
  type PeakType,
  type PdcPoint,
} from '../components/charts/PdcCurveChart';
import {
  buildStructureTimeline,
  evaluateSegmentCompliance,
  normalizeWorkoutStreamTimeline,
  resolveChartTimeBase,
  secToChartFrac,
  streamIndexToChartFrac,
  STRUCTURE_ZONE_COLORS,
  type StructureTimelineSegment,
  type StructureCompliance,
} from '../utils/workoutStructure';
import { useAthleteMetrics } from '../hooks/useAthleteMetrics';
import { usePinchZoom } from '../hooks/usePinchZoom';
import { usePointerPanelReorder } from '../hooks/usePointerPanelReorder';
import { attachWindowPointerDrag } from '../utils/windowPointerDrag';
import { useComparisonNavigation } from '../context/ComparisonNavigationContext';
import {
  type StreamPoint,
  extractStreamRaw,
  parseStreamData,
  computeRangeStats,
  buildSmoothedStream,
  smoothingWindow,
  extractGpsRoute,
} from '../utils/workoutStream';
import {
  findStreamIndicesForModalItem,
  findStreamIndicesForLap,
} from '../features/workout/workoutPeakHelpers';
import type { LapRow } from '../utils/comparison/lapSegments';
import {
  DebouncedInvalidateMapSize,
  FitBoundsOnce,
  MapScrollWheelGuard,
} from '../components/gpsMapUtils';
import { NmTabNav } from '../components/NmTabNav';
import { PerformanceGuideModal, type PerformanceGuideKey, type PerformanceGuideMetric } from '../features/workout/PerformanceMetricGuide';

// ── Design tokens ─────────────────────────────────────────────────────────────
const BG          = 'var(--surface-0)';
const metricInk = (color: string): string => {
  if (['#EA4782', '#BE1553', '#DF2063', '#7C3AED'].includes(color)) return 'var(--ds-text-link)';
  if (['#EF4444', '#F87171'].includes(color)) return 'var(--text-danger)';
  if (['#3CC67D', '#1FAE63'].includes(color)) return 'var(--text-success)';
  if (['#F5B740', '#E0981A', '#FF8A65'].includes(color)) return 'var(--text-warning)';
  if (color === '#4A90D9') return 'var(--text-info)';
  if (color === '#8A9BBF') return 'var(--fg-3)';
  return color;
};
const SUNKEN      = 'var(--surface-sunken)';
const CHART_BG    = 'var(--studio-surface, #0F1220)';
const PINK        = 'var(--palette-brand-ink)';
const PINK_DEEP   = 'var(--palette-brand-ink)';
const PINK_TEXT   = 'var(--palette-brand-ink)';
const HR_RED      = '#EF4444';
const GREEN       = '#3CC67D';
const GREEN_D     = '#1FAE63';
const GOLD        = '#F5B740';
const ORANGE      = '#FF8A65';
const BLUE        = '#4A90D9';
const PURPLE      = '#7C3AED';
const CARD_BG     = 'var(--surface-2)';
const TEXT        = 'var(--fg-1)';
const TEXT_MID    = 'var(--fg-3)';
const TEXT_MUTED  = 'var(--fg-3)';
const TEXT_FAINT  = 'var(--fg-3)';
/** Selected-range panel — charcoal, matches workout header accent */
const SELECTION_BG      = 'var(--studio-surface, #1A1F2B)';
const SELECTION_SURFACE = 'var(--studio-subtle, #252B38)';
const SELECTION_TEXT    = 'var(--studio-ink, #F0F2F7)';
const SELECTION_MUTED   = 'var(--studio-muted, #9AA1B1)';
/** Right-panel stat rows: bold labels, subdued values */
const DETAIL_LABEL = TEXT;
const DETAIL_VALUE = TEXT_MUTED;

const SH_HERO    = '-5px -5px 14px var(--nm-light), 5px 5px 14px var(--nm-dark)';
const SH_MD      = '-4px -4px 10px var(--nm-light), 4px 4px 10px var(--nm-dark)';
const SH_SM      = '-3px -3px 7px var(--nm-light), 3px 3px 7px var(--nm-dark)';
const SH_IN_SM   = 'inset 2px 2px 4px var(--nm-dark), inset -2px -2px 4px var(--nm-light)';
const SH_LIGHT   = '-5px -5px 12px var(--nm-light), 5px 5px 12px var(--nm-dark)';
const SH_TILE    = '-4px -4px 9px var(--nm-light), 4px 4px 9px var(--nm-dark)';
const SH_ICON_SM = '-1px -1px 3px var(--nm-light), 1px 1px 3px var(--nm-dark)';
const SH_TAB_BAR = 'inset 4px 4px 8px var(--nm-dark), inset -4px -4px 8px var(--nm-light)';
const SH_TAB_ACT = '-3px -3px 6px var(--nm-light), 3px 3px 6px var(--nm-dark)';
const SH_CHEV    = '-2px -2px 5px var(--nm-light), 2px 2px 5px var(--nm-dark)';

const HR_Z_GRADS = TRAINING_ZONE_GRADIENTS;
const PW_Z_GRADS = TRAINING_ZONE_GRADIENTS;
const PW_Z_DOTS = TRAINING_ZONE_COLORS;

// ── Types ─────────────────────────────────────────────────────────────────────
interface ChartSelection {
  startFrac: number;
  endFrac: number;
  startIdx: number;
  endIdx: number;
  /** Peak row: authoritative duration from interval field (e.g. 360 for 6m). */
  durationSec?: number;
  timeStartSec?: number;
  timeEndSec?: number;
  /** Peak row: FIT unix range for calculate-selected-range API. */
  apiStartUnix?: number;
  apiEndUnix?: number;
}

function chartSelectionEqual(a: ChartSelection | null, b: ChartSelection | null): boolean {
  if (!a || !b) return false;
  return a.startIdx === b.startIdx && a.endIdx === b.endIdx;
}

/** Build chart selection from stream indices (sidebar lap/peak clicks). */
function buildChartSelection(
  stream: StreamPoint[],
  totalTime: number,
  startIdx: number,
  endIdx: number,
): ChartSelection {
  const timeline = normalizeWorkoutStreamTimeline(stream, totalTime);
  const timeBase = resolveChartTimeBase(totalTime);
  const n = timeline.length;
  if (!n) return { startIdx: 0, endIdx: 0, startFrac: 0, endFrac: 0 };
  const si = Math.max(0, Math.min(startIdx, n - 1));
  const ei = Math.max(si, Math.min(endIdx, n - 1));
  return {
    startIdx: si,
    endIdx: ei,
    startFrac: streamIndexToChartFrac(si, timeline, timeBase),
    endFrac: streamIndexToChartFrac(ei, timeline, timeBase),
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function secToHMS(s: number): string {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return displayDigits(`${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`);
}
function fmtInterval(s: number): string {
  if (s < 60) return displayDigits(`${s}s`);
  const m = s / 60;
  if (s < 3600) return displayDigits(Number.isInteger(m) ? `${m}m` : `${Math.round(m)}m`);
  return displayDigits(`${(s / 3600).toFixed(1)}h`);
}
function fmtDisplayDate(iso: string): string {
  if (!iso) return 'This session';
  const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}
function fmtNum(v: unknown, dec = 0): string {
  const x = Number(v);
  return v != null && isFinite(x) ? displayDigits(dec ? x.toFixed(dec) : String(Math.round(x))) : '—';
}

// ── GPS Map (OpenStreetMap via Leaflet) ──────────────────────────────────────

// Fix Leaflet default icon paths (broken by bundlers)
// @ts-expect-error - Leaflet internals
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/** Fixed-size SVG circles as Leaflet divIcon markers — avoids bundler icon issues */
function startIcon() {
  return L.divIcon({
    className: '',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    html: `<svg width="20" height="20" viewBox="0 0 24 24" style="display:block">
      <circle cx="12" cy="12" r="9" fill="#1FAE63" stroke="white" stroke-width="2.5"/>
      <path d="M12 3v6M12 15v6M3 12h6M15 12h6" stroke="white" stroke-width="2" stroke-linecap="round" opacity="0"/>
    </svg>`,
  });
}

function endIcon() {
  return L.divIcon({
    className: '',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    html: `<svg width="20" height="20" viewBox="0 0 24 24" style="display:block">
      <circle cx="12" cy="12" r="9" fill="var(--palette-danger-fill)" stroke="white" stroke-width="2.5"/>
    </svg>`,
  });
}


function CompactMapAttribution() {
  const map = useMap();
  useEffect(() => { map.attributionControl.setPrefix(false); }, [map]);
  return null;
}

interface GpsMapProps {
  gpsRoute: [number, number][];
  gpsSelection?: [number, number][];
  height: number;
  fitKey?: string;
}

function GpsMap({ gpsRoute, gpsSelection, height, fitKey }: GpsMapProps) {
  const { t } = useTranslation();
  if (gpsRoute.length < 2) {
    return (
      <div style={{ height, flexShrink: 0, background: SUNKEN, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: TEXT_MID }}>{t('workoutDetail.map.noGps')}</span>
      </div>
    );
  }

  const center = gpsRoute[Math.floor(gpsRoute.length / 2)];
  const hasSelection = gpsSelection && gpsSelection.length >= 2;

  return (
    <div style={{ height, flexShrink: 0, position: 'relative' }}>
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
      >
        <ResilientMapTiles />
        <CompactMapAttribution />
        <MapScrollWheelGuard />
        <DebouncedInvalidateMapSize height={height} />

        {/* Full route — dimmed when a selection is active */}
        <Polyline
          positions={gpsRoute}
          pathOptions={{
            color: hasSelection ? 'rgba(74,144,217,0.30)' : '#4A90D9',
            weight: hasSelection ? 2.5 : 3.5,
            opacity: 0.85,
            lineCap: 'round',
            lineJoin: 'round',
          }}
        />

        {/* Highlighted selection overlay */}
        {hasSelection && (
          <>
            {/* Glow under selection */}
            <Polyline
              positions={gpsSelection}
              pathOptions={{
                color: 'color-mix(in srgb, var(--text-accent) 30%, transparent)',
                weight: 8,
                opacity: 1,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
            {/* Selection stroke — bold pink */}
            <Polyline
              positions={gpsSelection}
              pathOptions={{
                color: metricInk(PINK),
                weight: 3.5,
                opacity: 0.95,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
          </>
        )}

        {/* Route glow (only when no selection) */}
        {!hasSelection && (
          <Polyline
            positions={gpsRoute}
            pathOptions={{
              color: 'rgba(74,144,217,0.25)',
              weight: 7,
              opacity: 1,
              lineCap: 'round',
              lineJoin: 'round',
            }}
          />
        )}

        {/* Start marker */}
        <Marker position={gpsRoute[0]} icon={startIcon()}>
          <Popup>Start</Popup>
        </Marker>

        {/* End marker */}
        <Marker position={gpsRoute[gpsRoute.length - 1]} icon={endIcon()}>
          <Popup>End</Popup>
        </Marker>

        <FitBoundsOnce positions={gpsRoute} fitKey={fitKey ?? 'default'} />
      </MapContainer>
    </div>
  );
}

// ── Workout Chart ─────────────────────────────────────────────────────────────
const SERIES = [
  { key: 'pwr' as const,  label: 'workoutDetail.map.seriesPower',     color: PINK_TEXT },
  { key: 'hr'  as const,  label: 'workoutDetail.map.seriesHr',        color: HR_RED   },
  { key: 'cad' as const,  label: 'workoutDetail.map.seriesCadence',   color: '#E0981A'},
  { key: 'spd' as const,  label: 'workoutDetail.map.seriesSpeed',     color: BLUE     },
  { key: 'elev' as const, label: 'workoutDetail.map.seriesElevation', color: '#8A9BBF'},
] as const;
type SeriesKey = 'pwr' | 'hr' | 'cad' | 'spd' | 'elev';

const COMPLIANCE_STROKE: Record<StructureCompliance, string> = {
  on_target: 'rgba(22,163,74,0.55)',
  under:     'rgba(249,115,22,0.55)',
  over:      'rgba(255,255,255,0.35)',
  unknown:   'rgba(255,255,255,0.12)',
};

/** Long-press arms region selection; short tap clears an active selection. */
const CHART_LONG_PRESS_MS = 450;
const CHART_TAP_MAX_MS = 280;
const CHART_TAP_MAX_MOVE_PX = 14;

interface WorkoutChartProps {
  running?: boolean;
  stream: StreamPoint[];
  totalTime: number;
  plannedTotalSec?: number;
  selection: ChartSelection | null;
  onSelectionChange: (sel: ChartSelection | null) => void;
  streamLoading?: boolean;
  streamError?: string | null;
  structureSegments?: StructureTimelineSegment[];
  ftp?: number | null;
}

/** Find planned block at elapsed seconds. */
function findStructureSegmentAtTime(
  segments: StructureTimelineSegment[],
  timeSec: number,
): StructureTimelineSegment | null {
  for (const seg of segments) {
    if (timeSec >= seg.startSec && timeSec < seg.endSec) return seg;
  }
  if (segments.length && timeSec >= segments[segments.length - 1].endSec) {
    return segments[segments.length - 1];
  }
  return null;
}

function WorkoutChart({
  running = false,
  stream,
  totalTime,
  plannedTotalSec = 0,
  selection,
  onSelectionChange,
  streamLoading,
  streamError,
  structureSegments = [],
  ftp = null,
}: WorkoutChartProps) {
  const { t } = useTranslation();
  const [vis, setVis] = useState<Record<SeriesKey, boolean>>({ pwr: true, hr: true, cad: true, spd: true, elev: true });
  const [showStructure, setShowStructure] = useState(true);
  const [smoothLevel, setSmoothLevel] = useState(30);
  const [size, setSize] = useState({ w: 900, h: 360 });
  const [hover, setHover] = useState<{ frac: number; idx: number } | null>(null);
  const [dragSel, setDragSel] = useState<{ start: number; end: number } | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragStartFrac = useRef(0);
  const activePointerId = useRef<number | null>(null);
  const pointerStartRef = useRef({ x: 0, y: 0, t: 0 });
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDragSelectRef = useRef(false);
  const pinchActiveRef = useRef(false);

  // Zoom state — visible window as fraction of total time
  const [zoomStartFrac, setZoomStartFrac] = useState(0);
  const [zoomEndFrac, setZoomEndFrac]   = useState(1);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const obs = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width > 50 && height > 50) setSize({ w: Math.round(width), h: Math.round(height) });
    });
    obs.observe(wrap);
    return () => obs.disconnect();
  }, []);

  // Reset zoom when workout changes
  useEffect(() => {
    setZoomStartFrac(0);
    setZoomEndFrac(1);
  }, [stream]);

  const timelineStream = useMemo(
    () => normalizeWorkoutStreamTimeline(stream, totalTime),
    [stream, totalTime],
  );

  const hasStructure = structureSegments.length > 0;

  const chartTimeBase = useMemo(
    () => resolveChartTimeBase(totalTime),
    [totalTime],
  );

  /** Fraction on chart where the planned workout ends (e.g. 1:53 on a 2:17 ride). */
  const plannedEndFrac = useMemo(() => {
    if (!plannedTotalSec || plannedTotalSec <= 0) return null;
    return secToChartFrac(plannedTotalSec, chartTimeBase);
  }, [plannedTotalSec, chartTimeBase]);

  const segmentCompliance = useMemo(() => {
    const map = new Map<number, StructureCompliance>();
    if (!hasStructure) return map;
    structureSegments.forEach(seg => {
      map.set(seg.index, evaluateSegmentCompliance(timelineStream, seg, ftp));
    });
    return map;
  }, [hasStructure, structureSegments, timelineStream, ftp]);

  const structureOverlay = useMemo(() => {
    if (!hasStructure) return [];
    return structureSegments.map(seg => {
      const start = secToChartFrac(seg.startSec, chartTimeBase);
      const end = secToChartFrac(seg.endSec, chartTimeBase);
      const compliance = segmentCompliance.get(seg.index) ?? 'unknown';
      const zoneColor = STRUCTURE_ZONE_COLORS[seg.zone] ?? STRUCTURE_ZONE_COLORS[3];
      return { seg, start, end, compliance, zoneColor };
    });
  }, [hasStructure, structureSegments, chartTimeBase, segmentCompliance, ftp]);

  const N = timelineStream.length;
  const smoothWin = useMemo(() => smoothingWindow(smoothLevel, N), [smoothLevel, N]);
  const chartStream = useMemo(() => buildSmoothedStream(timelineStream, smoothWin), [timelineStream, smoothWin]);
  const ML = 48, MR = 38, MT = 14, MB = 28;
  const CW = Math.max(1, size.w - ML - MR);
  const CH = Math.max(1, size.h - MT - MB);
  const zoomSpan  = zoomEndFrac - zoomStartFrac;
  const isZoomed  = zoomStartFrac > 0.001 || zoomEndFrac < 0.999;

  // Convert absolute time fraction → screen X coordinate
  const XiFrac = useCallback((frac: number) => ML + ((frac - zoomStartFrac) / zoomSpan) * CW, [ML, CW, zoomStartFrac, zoomSpan]);

  // Convert stream index → chart fraction (time-based, matches axis labels + plan overlay)
  const indexToFrac = useCallback(
    (i: number) => streamIndexToChartFrac(i, timelineStream, chartTimeBase),
    [timelineStream, chartTimeBase],
  );

  // Helper: screen position for stream index
  const Xi = useCallback((i: number) => XiFrac(indexToFrac(i)), [XiFrac, indexToFrac]);

  // Visible stream index range
  const visRange = useMemo(() => {
    const si = Math.max(0, Math.floor(zoomStartFrac * (N - 1)));
    const ei = Math.min(N - 1, Math.ceil(zoomEndFrac * (N - 1)));
    return { start: si, end: ei };
  }, [zoomStartFrac, zoomEndFrac, N]);

  // Elevation bounds (from smoothed display data)
  const elevVals = useMemo(() => chartStream.map(p => p.altitude).filter((v): v is number => v != null), [chartStream]);
  const elevMin = elevVals.length ? Math.min(...elevVals) - 15 : -50;
  const elevMax = elevVals.length ? Math.max(...elevVals) + 20 : 800;

  // Scale functions — only affected by vertical range, unchanged by zoom
  const Yp = useCallback((p: number) => MT + CH - (Math.max(0, Math.min(520, p)) / 520) * CH, [CH]);
  const Yh = useCallback((h: number) => MT + CH - ((Math.max(55, Math.min(195, h)) - 55) / 140) * CH, [CH]);
  const cadenceMax = useMemo(() => Math.ceil(chartStream.reduce((max, p) => Math.max(max, p.cadence ?? 0), 155) * 1.1 / 10) * 10, [chartStream]);
  const Yc = useCallback((c: number) => MT + CH - (Math.max(0, c) / cadenceMax) * CH, [CH, cadenceMax]);
  const Ys = useCallback((s: number) => MT + CH - (Math.max(0, Math.min(72, s)) / 72) * CH, [CH]);
  const Ye = useCallback((e: number) => MT + CH - ((Math.max(elevMin, Math.min(elevMax, e)) - elevMin) / (elevMax - elevMin || 1)) * CH * 0.46, [CH, elevMin, elevMax]);

  // Path generation — only visible segment
  const paths = useMemo(() => {
    if (!N) return { pwr: '', hr: '', cad: '', spd: '', elev: '' };
    const { start, end } = visRange;
    function mkLine(vals: (number | null)[], scale: (v: number) => number): string {
      let d = '', drawing = false;
      for (let i = start; i <= end; i++) {
        const v = vals[i];
        if (v == null || !isFinite(v)) { drawing = false; continue; }
        const x = Xi(i).toFixed(1), y = scale(v).toFixed(1);
        d += drawing ? ` L${x},${y}` : `M${x},${y}`;
        drawing = true;
      }
      return d;
    }
    // Extend elevation fill slightly left/right for seamless fill
    const elevFill = buildStreamMetricPath(chartStream, chartStream.map(p => p.altitude), Xi, Ye, {
      startIdx: start, endIdx: end, baselineY: MT + CH,
    });
    return {
      pwr: mkLine(chartStream.map(p => p.power), Yp),
      hr:  mkLine(chartStream.map(p => p.heartRate), Yh),
      cad: mkLine(chartStream.map(p => p.cadence), Yc),
      spd: mkLine(chartStream.map(p => p.speed), Ys),
      elev: elevFill,
    };
  }, [chartStream, N, visRange, Yp, Yh, Yc, Ys, Ye, Xi, ML, CW, MT, CH]);

  // Grid & axes
  const gridLines = useMemo(() => {
    const lines: React.ReactNode[] = [];
    [0, 100, 200, 300, 400, 500].forEach(p => {
      const y = Yp(p);
      if (y < MT - 2 || y > MT + CH + 2) return;
      lines.push(<line key={`g${p}`} x1={ML} y1={y} x2={ML + CW} y2={y} stroke="var(--chart-grid, rgba(255,255,255,.05))" strokeWidth="1"/>);
      lines.push(<text key={`l${p}`} x={ML - 5} y={y + 3.5} textAnchor="end" fontSize="9" fill="var(--ds-text-link)" fontFamily="system-ui,sans-serif">{displayDigits(p)}</text>);
    });
    [80, 110, 140, 170].forEach(h => {
      const y = Yh(h);
      if (y < MT - 2 || y > MT + CH + 2) return;
      lines.push(<text key={`hr${h}`} x={ML + CW + 5} y={y + 3.5} textAnchor="start" fontSize="9" fill="var(--text-danger)" fontFamily="system-ui,sans-serif">{displayDigits(h)}</text>);
    });
    return lines;
  }, [Yp, Yh, ML, CW, MT, CH]);

  const timeLabels = useMemo(() => {
    if (!chartTimeBase) return [];
    const labels: React.ReactNode[] = [];
    // Adaptive step based on visible time range
    const visibleTime = chartTimeBase * zoomSpan;
    const step = visibleTime < 60 ? 10
               : visibleTime < 180 ? 30
               : visibleTime < 600 ? 60
               : visibleTime < 1800 ? 300
               : visibleTime < 5400 ? 600
               : 1800;
    const tStart = Math.max(0, Math.floor(zoomStartFrac * chartTimeBase / step) * step);
    const tEnd = Math.min(chartTimeBase, Math.ceil(zoomEndFrac * chartTimeBase / step) * step);
    for (let t = tStart; t <= tEnd; t += step) {
      const x = XiFrac(t / chartTimeBase).toFixed(1);
      labels.push(<text key={t} x={x} y={size.h - 6} textAnchor="middle" fontSize="9" fill="var(--chart-label, rgba(255,255,255,.2))" fontFamily="system-ui,sans-serif">{secToHMS(t)}</text>);
    }
    return labels;
  }, [chartTimeBase, zoomSpan, zoomStartFrac, zoomEndFrac, XiFrac, size.h]);

  // Touch / pointer helpers
  /** Absolute fraction (0..1 over full workout) from screen X */
  function getAbsFracFromX(clientX: number): number {
    const wrap = wrapRef.current;
    if (!wrap) return 0;
    const r = wrap.getBoundingClientRect();
    const screenFrac = Math.max(0, Math.min(1, (clientX - r.left - ML) / CW));
    return zoomStartFrac + screenFrac * zoomSpan;
  }
  function fracToIdx(frac: number): number {
    if (!N) return 0;
    const targetSec = frac * chartTimeBase;
    let bestIdx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < N; i++) {
      const t = timelineStream[i].timeSeconds;
      const diff = Math.abs(t - targetSec);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  const applyPinchZoom = useCallback(
    (factor: number, centerClientX: number) => {
      const frac = getAbsFracFromX(centerClientX);
      const span = zoomSpan;
      let newSpan = Math.max(0.0015, Math.min(1, span / factor));
      const ratio = (frac - zoomStartFrac) / span;
      let newStart = frac - ratio * newSpan;
      let newEnd = newStart + newSpan;
      if (newStart < 0) { newEnd += -newStart; newStart = 0; }
      if (newEnd > 1) { newStart -= (newEnd - 1); newEnd = 1; }
      newStart = Math.max(0, newStart);
      newEnd = Math.min(1, newEnd);
      setZoomStartFrac(newStart);
      setZoomEndFrac(newEnd);
    },
    [zoomSpan, zoomStartFrac],
  );

  usePinchZoom(wrapRef, applyPinchZoom, N > 0);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const cancelSelectDrag = useCallback(() => {
    isDragSelectRef.current = false;
    setDragSel(null);
  }, []);

  /** Two-finger pinch cancels scrub / long-press so zoom stays responsive. */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || N < 1) return;

    const onTouchChange = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        pinchActiveRef.current = true;
        clearLongPress();
        cancelSelectDrag();
        activePointerId.current = null;
        setIsScrubbing(false);
      } else if (e.touches.length === 0) {
        pinchActiveRef.current = false;
      }
    };

    el.addEventListener('touchstart', onTouchChange, { passive: true });
    el.addEventListener('touchend', onTouchChange, { passive: true });
    el.addEventListener('touchcancel', onTouchChange, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchChange);
      el.removeEventListener('touchend', onTouchChange);
      el.removeEventListener('touchcancel', onTouchChange);
    };
  }, [N, clearLongPress, cancelSelectDrag]);

  // Wheel → zoom in/out (desktop fallback; Ctrl/Cmd required so scroll passes through)
  function handleWheel(e: React.WheelEvent) {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const frac = getAbsFracFromX(e.clientX);
    const span = zoomSpan;
    const factor = e.deltaY < 0 ? 0.72 : 1.39;
    let newSpan = Math.max(0.0015, Math.min(1, span * factor));
    const ratio = (frac - zoomStartFrac) / span;
    let newStart = frac - ratio * newSpan;
    let newEnd = newStart + newSpan;
    if (newStart < 0) { newEnd += -newStart; newStart = 0; }
    if (newEnd > 1) { newStart -= (newEnd - 1); newEnd = 1; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(1, newEnd);
    setZoomStartFrac(newStart);
    setZoomEndFrac(newEnd);
  }

  function updateHoverAtX(clientX: number) {
    const frac = getAbsFracFromX(clientX);
    setHover({ frac, idx: fracToIdx(frac) });
  }

  function handlePointerDown(e: React.PointerEvent) {
    if (N < 1 || pinchActiveRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    activePointerId.current = e.pointerId;
    pointerStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    setIsScrubbing(true);

    const frac = getAbsFracFromX(e.clientX);
    setHover({ frac, idx: fracToIdx(frac) });

    clearLongPress();
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      if (pinchActiveRef.current || activePointerId.current !== e.pointerId) return;
      isDragSelectRef.current = true;
      dragStartFrac.current = frac;
      setDragSel({ start: frac, end: frac });
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(12);
      }
    }, CHART_LONG_PRESS_MS);

    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* */ }
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (N < 1) return;

    if (e.pointerType === 'mouse' && e.buttons === 0) {
      updateHoverAtX(e.clientX);
      return;
    }

    if (activePointerId.current !== e.pointerId || pinchActiveRef.current) return;

    const dx = e.clientX - pointerStartRef.current.x;
    const dy = e.clientY - pointerStartRef.current.y;

    updateHoverAtX(e.clientX);

    if (!isDragSelectRef.current && (Math.abs(dx) > CHART_TAP_MAX_MOVE_PX || Math.abs(dy) > CHART_TAP_MAX_MOVE_PX)) {
      clearLongPress();
    }

    if (isDragSelectRef.current) {
      const frac = getAbsFracFromX(e.clientX);
      const s = Math.min(dragStartFrac.current, frac);
      const en = Math.max(dragStartFrac.current, frac);
      setDragSel({ start: s, end: en });
    }
  }

  function finishSelectDrag(clientX: number) {
    if (!isDragSelectRef.current) return;
    isDragSelectRef.current = false;
    const frac = getAbsFracFromX(clientX);
    const s = Math.min(dragStartFrac.current, frac);
    const en = Math.max(dragStartFrac.current, frac);
    setDragSel(null);
    if (en - s > 0.004 * zoomSpan) {
      onSelectionChange({ startFrac: s, endFrac: en, startIdx: fracToIdx(s), endIdx: fracToIdx(en) });
    }
  }

  function handlePointerUp(e: React.PointerEvent) {
    if (activePointerId.current !== e.pointerId) return;

    clearLongPress();
    const elapsed = Date.now() - pointerStartRef.current.t;
    const moved = Math.hypot(
      e.clientX - pointerStartRef.current.x,
      e.clientY - pointerStartRef.current.y,
    );

    if (isDragSelectRef.current) {
      finishSelectDrag(e.clientX);
    } else if (elapsed < CHART_TAP_MAX_MS && moved < CHART_TAP_MAX_MOVE_PX && selection) {
      onSelectionChange(null);
    }

    updateHoverAtX(e.clientX);
    activePointerId.current = null;
    setIsScrubbing(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* */ }
  }

  function handlePointerCancel(e: React.PointerEvent) {
    clearLongPress();
    if (isDragSelectRef.current) {
      isDragSelectRef.current = false;
      setDragSel(null);
    }
    activePointerId.current = null;
    setIsScrubbing(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* */ }
  }

  function handlePointerLeave(e: React.PointerEvent) {
    if (e.pointerType === 'mouse') setHover(null);
    if (activePointerId.current === e.pointerId) {
      clearLongPress();
      if (isDragSelectRef.current) {
        isDragSelectRef.current = false;
        setDragSel(null);
      }
      activePointerId.current = null;
      setIsScrubbing(false);
    }
  }

  // Active selection for chart display
  const activeSel = dragSel ?? (selection ? { start: selection.startFrac, end: selection.endFrac } : null);
  const hoverPt = hover && N > 0 ? timelineStream[hover.idx] : null;
  const hoverStructureSeg = hoverPt && showStructure && hasStructure
    ? findStructureSegmentAtTime(structureSegments, hoverPt.timeSeconds)
    : null;

  const selX1 = activeSel ? XiFrac(activeSel.start) : 0;
  const selX2 = activeSel ? XiFrac(activeSel.end) : 0;
  const selW  = selX2 - selX1;

  return (
    <div style={{ flex: 1, minHeight: 0, background: CHART_BG, display: 'flex', flexDirection: 'column' }}>
      {/* Smoothing slider */}
      <div className="analysis-smoothing" style={{ flexShrink: 0, height: 26, display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', color: 'var(--studio-muted, rgba(255,255,255,.22))', textTransform: 'uppercase', flexShrink: 0 }}>{t('workoutDetail.map.smoothLevel', { pct: smoothLevel })}</span>
        <input
          type="range"
          min={0}
          max={100}
          value={smoothLevel}
          onChange={e => setSmoothLevel(Number(e.target.value))}
          title={t('analysis.chartText.smoothHint')}
          style={{ flex: 1, maxWidth: 140, height: 3, accentColor: PINK_TEXT, cursor: 'pointer' }}
        />
        <span style={{ fontSize: 8, fontWeight: 700, color: 'var(--studio-muted, rgba(255,255,255,.28))', minWidth: 36, fontVariantNumeric: 'tabular-nums' }}>
          {smoothLevel === 0 ? t('analysis.chartText.off') : displayDigits(t('analysis.chartText.points', { count: smoothWin }))}
        </span>
      </div>

      {/* Legend bar */}
      <div className="analysis-chart-toolbar" style={{ flexShrink: 0, height: 36, display: 'flex', alignItems: 'center', gap: 3, padding: '0 14px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', color: 'var(--studio-muted, rgba(255,255,255,.22))', textTransform: 'uppercase', marginRight: 4 }}>{t('analysis.chartText.show')}</span>
        {SERIES.map(s => (
          <ActionButton
            key={s.key}
            type="button"
            aria-pressed={vis[s.key]}
            onClick={() => setVis(v => ({ ...v, [s.key]: !v[s.key] }))}
          ><div style={{ width: 6, height: 6, borderRadius: s.key === 'elev' ? 2 : '50%', background: s.color, flexShrink: 0 }}/>
            {t(s.key === 'cad' && stream.some(p => p.cadenceUnit === 'spm') ? 'runningMetrics.cadence' : s.label)}</ActionButton>
        ))}
        {hasStructure && (
          <ActionButton
            type="button"
            aria-pressed={showStructure}
            onClick={() => setShowStructure(v => !v)}
            title={t('workoutDetail.map.seriesStructure')}
          ><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="8" width="7" height="13" rx="1"/>
            </svg>
            {t('workoutDetail.map.seriesStructure')}</ActionButton>
        )}
        {/* Reset Zoom */}
        {isZoomed && (
          <ActionButton
            onClick={() => { setZoomStartFrac(0); setZoomEndFrac(1); }}
            style={{ marginLeft: 'auto' }}
          ><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
            {t('workoutDetail.map.resetZoom')}</ActionButton>
        )}
        {selection && (
          <>
            <ActionButton
              onClick={() => onSelectionChange(null)}
            ><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            {t('workoutDetail.map.clearSelection')}</ActionButton>
          </>
        )}
      </div>

      {/* Chart SVG */}
      <div
        ref={wrapRef}
        className={`chart-touch-plot pinch-zoom-zone${isScrubbing ? ' chart-touch-plot--scrubbing' : ''}`}
        style={{ flex: 1, minHeight: 0, position: 'relative', userSelect: 'none', touchAction: 'none', overflow: 'hidden' }}
        onPointerDown={N > 0 ? handlePointerDown : undefined}
        onPointerMove={N > 0 ? handlePointerMove : undefined}
        onPointerUp={N > 0 ? handlePointerUp : undefined}
        onPointerCancel={N > 0 ? handlePointerCancel : undefined}
        onPointerLeave={N > 0 ? handlePointerLeave : undefined}
        onWheel={N > 0 ? handleWheel : undefined}
      >
        {N === 0 ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--studio-muted, rgba(255,255,255,.25))', fontSize: 11, fontWeight: 600, padding: 16, textAlign: 'center' }}>
            {streamLoading ? <NmLoader label={t('analysis.loading')} /> : streamError ?? t('analysis.streamNoData')}
          </div>
        ) : (
        <>
        <svg width={size.w} height={size.h} style={{ display: 'block', width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="chartBg2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#111421"/><stop offset="100%" stopColor="#0D0F18"/>
            </linearGradient>
            <linearGradient id="elevFill2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5C7098" stopOpacity="0.55"/><stop offset="100%" stopColor="#5C7098" stopOpacity="0.02"/>
            </linearGradient>
            <clipPath id="chartClip"><rect x={ML} y={MT} width={CW} height={CH}/></clipPath>
          </defs>

          {/* Background */}
          <rect width={size.w} height={size.h} fill="url(#chartBg2)"/>

          {/* Grid & axes */}
          {gridLines}

          {/* Planned structure blocks — power-height shapes (aligned to left Y axis) */}
          {showStructure && hasStructure && structureOverlay.map(({ seg, start, end, compliance, zoneColor }) => {
            if (end <= zoomStartFrac || start >= zoomEndFrac) return null;
            const x1 = XiFrac(start);
            const x2 = XiFrac(end);
            const w = Math.max(x2 - x1, 1);
            const yBase = MT + CH;

            const ftpRef = ftp && ftp > 0 ? ftp : 250;
            const pctToWatts = (pct: number) => Math.max(0, Math.min(520, (ftpRef * pct) / 100));
            const wMin = pctToWatts(seg.minPct);
            const wMax = pctToWatts(seg.maxPct);
            const clampY = (y: number) => Math.max(MT, Math.min(yBase, y));

            const fill = compliance === 'unknown'
              ? `color-mix(in srgb, ${zoneColor} 33%, transparent)`
              : compliance === 'on_target' ? 'rgba(22,163,74,0.42)'
              : compliance === 'under' ? 'rgba(249,115,22,0.42)'
              : 'rgba(26,31,43,0.55)';
            const stroke = compliance === 'unknown'
              ? `color-mix(in srgb, ${zoneColor} 67%, transparent)`
              : COMPLIANCE_STROKE[compliance];

            let shapeTop = yBase;
            let shape: React.ReactNode;

            if (seg.isRamp) {
              const growing = seg.minPct <= seg.maxPct;
              const yLeft = clampY(Yp(growing ? wMin : wMax));
              const yRight = clampY(Yp(growing ? wMax : wMin));
              shapeTop = Math.min(yLeft, yRight);
              const pts = `${x1.toFixed(1)},${yBase} ${x1.toFixed(1)},${yLeft.toFixed(1)} ${x2.toFixed(1)},${yRight.toFixed(1)} ${x2.toFixed(1)},${yBase}`;
              shape = (
                <polygon
                  points={pts}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1"
                  clipPath="url(#chartClip)"
                />
              );
            } else {
              const yTop = clampY(Yp(Math.max(wMin, wMax)));
              const yBot = clampY(Yp(Math.min(wMin, wMax)));
              shapeTop = yTop;
              const barH = Math.max(yBot - yTop, 2);
              shape = (
                <rect
                  x={x1 + 0.5}
                  y={yTop}
                  width={Math.max(w - 1, 1)}
                  height={barH}
                  rx={Math.min(3, Math.max(w - 1, 1) / 2)}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1"
                  clipPath="url(#chartClip)"
                />
              );
            }

            const pctLabelY = Math.max(MT + 12, shapeTop - 2);

            return (
              <g key={`struct-${seg.index}`}>
                {shape}
                {w >= 22 && shapeTop > MT + 14 && (
                  <text
                    x={x1 + w / 2}
                    y={pctLabelY}
                    textAnchor="middle"
                    fontSize="7"
                    fontWeight="600"
                    fill="rgba(255,255,255,0.35)"
                    fontFamily="system-ui,sans-serif"
                    clipPath="url(#chartClip)"
                  >
                    {displayDigits(seg.minPct)}–{displayDigits(seg.maxPct)}%
                  </text>
                )}
              </g>
            );
          })}

          {/* Post-plan region — actual workout continued beyond planned duration */}
          {showStructure && plannedEndFrac != null && plannedEndFrac < 0.999 && plannedEndFrac > zoomStartFrac && (
            <>
              <rect
                x={XiFrac(Math.max(plannedEndFrac, zoomStartFrac))}
                y={MT}
                width={Math.max(0, XiFrac(Math.min(1, zoomEndFrac)) - XiFrac(Math.max(plannedEndFrac, zoomStartFrac)))}
                height={CH}
                fill="rgba(0,0,0,0.18)"
                clipPath="url(#chartClip)"
              />
              <line
                x1={XiFrac(plannedEndFrac)}
                y1={MT}
                x2={XiFrac(plannedEndFrac)}
                y2={MT + CH}
                stroke="rgba(245,183,64,0.65)"
                strokeWidth="1.5"
                strokeDasharray="6,4"
                clipPath="url(#chartClip)"
              />
              {plannedTotalSec > 0 && XiFrac(plannedEndFrac) - ML > 36 && (
                <text
                  x={XiFrac(plannedEndFrac) - 4}
                  y={MT + 10}
                  textAnchor="end"
                  fontSize="7.5"
                  fontWeight="700"
                  fill="rgba(245,183,64,0.55)"
                  fontFamily="system-ui,sans-serif"
                  clipPath="url(#chartClip)"
                >
                  Plan {secToHMS(plannedTotalSec)}
                </text>
              )}
            </>
          )}

          {/* Series paths */}
          {vis.elev  && paths.elev && <path d={paths.elev} fill="url(#elevFill2)" clipPath="url(#chartClip)"/>}
          {vis.cad   && paths.cad  && <path d={paths.cad}  fill="none" stroke="#E0981A" strokeWidth="1.2" opacity="0.65" clipPath="url(#chartClip)" strokeLinejoin="round"/>}
          {vis.spd   && paths.spd  && <path d={paths.spd}  fill="none" stroke={BLUE}    strokeWidth="1.4" opacity="0.72" clipPath="url(#chartClip)" strokeLinejoin="round"/>}
          {vis.pwr   && paths.pwr  && <path d={paths.pwr}  fill="none" stroke={PINK_TEXT} strokeWidth="1.5" opacity="0.9"  clipPath="url(#chartClip)" strokeLinejoin="round"/>}
          {vis.hr    && paths.hr   && <path d={paths.hr}   fill="none" stroke={HR_RED} strokeWidth="2.1" opacity="0.92" clipPath="url(#chartClip)" strokeLinejoin="round"/>}

          {/* Time labels */}
          {timeLabels}

          {/* Axis border lines */}
          <line x1={ML} y1={MT} x2={ML} y2={MT+CH} stroke="var(--studio-border)" strokeWidth="1"/>
          <line x1={ML+CW} y1={MT} x2={ML+CW} y2={MT+CH} stroke="var(--studio-border)" strokeWidth="1"/>
          <line x1={ML} y1={MT+CH} x2={ML+CW} y2={MT+CH} stroke="var(--studio-border)" strokeWidth="1"/>

          {/* Selection overlay — dim unselected regions */}
          {activeSel && selW > 0 && (
            <>
              <rect x={ML} y={MT} width={Math.max(0, selX1 - ML)} height={CH} fill="rgba(0,0,0,.38)" clipPath="url(#chartClip)"/>
              <rect x={selX2} y={MT} width={Math.max(0, (ML+CW) - selX2)} height={CH} fill="rgba(0,0,0,.38)" clipPath="url(#chartClip)"/>
              <rect x={selX1} y={MT} width={selW} height={CH} fill="color-mix(in srgb, var(--text-accent) 10%, transparent)" clipPath="url(#chartClip)"/>
              <line x1={selX1} y1={MT} x2={selX1} y2={MT+CH} stroke="color-mix(in srgb, var(--text-accent) 60%, transparent)" strokeWidth="1.5"/>
              <line x1={selX2} y1={MT} x2={selX2} y2={MT+CH} stroke="color-mix(in srgb, var(--text-accent) 60%, transparent)" strokeWidth="1.5"/>
              <circle cx={selX1} cy={MT+CH/2} r="4" fill={PINK} opacity="0.8"/>
              <circle cx={selX2} cy={MT+CH/2} r="4" fill={PINK} opacity="0.8"/>
            </>
          )}

          {/* Hover cursor */}
          {hover && (
            <line x1={XiFrac(hover.frac)} y1={MT} x2={XiFrac(hover.frac)} y2={MT+CH} stroke="var(--studio-border)" strokeWidth="1"/>
          )}
        </svg>

        {/* Hover tooltip */}
        {hoverPt && hover && (
          <HoverTooltip running={running}
            pt={hoverPt}
            hoverX={XiFrac(hover.frac)}
            vis={vis}
            containerW={size.w}
            ML={ML}
            MT={MT}
            CH={CH}
            structureSeg={hoverStructureSeg}
            structureCompliance={hoverStructureSeg ? segmentCompliance.get(hoverStructureSeg.index) : undefined}
            ftp={ftp}
          />
        )}
        </>
        )}
      </div>
    </div>
  );
}

function HoverTooltip({ running = false, pt, hoverX, vis, containerW, ML, MT, CH, structureSeg, structureCompliance, ftp }: {
  running?: boolean;
  pt: StreamPoint;
  hoverX: number;
  vis: Record<SeriesKey, boolean>;
  containerW: number;
  ML: number;
  MT: number;
  CH: number;
  structureSeg?: StructureTimelineSegment | null;
  structureCompliance?: StructureCompliance;
  ftp?: number | null;
}) {
  const { t } = useTranslation();
  const rows = [
    vis.pwr  && pt.power     != null ? { label: t('workoutDetail.map.seriesPower'),     val: `${Math.round(pt.power)} W`,         color: metricInk(PINK_TEXT) } : null,
    vis.hr   && pt.heartRate != null ? { label: t('workoutDetail.map.seriesHr'),        val: `${Math.round(pt.heartRate)} bpm`,   color: metricInk(HR_RED)    } : null,
    vis.cad  && pt.cadence   != null ? { label: t(pt.cadenceUnit === 'spm' ? 'runningMetrics.cadence' : 'workoutDetail.map.seriesCadence'),   val: `${Math.round(pt.cadence)} ${pt.cadenceUnit === 'spm' ? t('runningMetrics.cadenceUnit') : 'rpm'}`,     color: '#E0981A' } : null,
    vis.spd && pt.speed != null ? { label: t('workoutDetail.map.speed'), val: `${pt.speed.toFixed(1)} km/h`, color: metricInk(BLUE) } : null,
    running && vis.spd && pt.speed != null && Number.isFinite(pt.speed) ? { label: t('runningMetrics.paceLabel'), val: `${paceFromKmh(pt.speed)} ${t('runningMetrics.paceUnit')}`, color: metricInk(BLUE) } : null,
    vis.elev && pt.altitude  != null ? { label: t('workoutDetail.map.seriesElevation'), val: `${Math.round(pt.altitude)} m`,      color: '#8A9BBF' } : null,
  ].filter(Boolean) as { label: string; val: string; color: string }[];

  const complianceLabel: Record<StructureCompliance, string> = {
    on_target: t('analysis.chartText.onPlan'),
    under: t('analysis.chartText.belowPlan'),
    over: t('analysis.chartText.abovePlan'),
    unknown: '—',
  };
  const complianceColor: Record<StructureCompliance, string> = {
    on_target: GREEN_D,
    under: ORANGE,
    over: '#9ca3af',
    unknown: TEXT_MUTED,
  };

  const tooltipW = structureSeg ? 168 : 148;
  const left = hoverX + 14 + tooltipW > containerW ? hoverX - tooltipW - 14 : hoverX + 14;
  const top = MT + CH * 0.15;

  return (
    <div className="analysis-tooltip" style={{ position: 'absolute', left, top, width: tooltipW, background: 'var(--studio-surface, rgba(8,11,24,.94))', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 10, padding: '9px 11px', pointerEvents: 'none', boxShadow: '0 8px 24px rgba(0,0,0,.5)', zIndex: 30 }}>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', color: 'var(--studio-muted, rgba(255,255,255,.3))', marginBottom: 5, fontVariantNumeric: 'tabular-nums' }}>{secToHMS(pt.timeSeconds)}</div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '2px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 8.5, fontWeight: 700, color: TEXT_MUTED, letterSpacing: '0.04em' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: r.color, flexShrink: 0 }}/>
            {r.label}
          </div>
          <span style={{ fontSize: 11, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: metricInk(r.color) }}>{displayDigits(r.val)}</span>
        </div>
      ))}
      {structureSeg && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,.08)' }}>
          <div style={{ fontSize: 8.5, fontWeight: 600, color: TEXT_MUTED }}>
            {displayDigits(structureSeg.minPct)}–{displayDigits(structureSeg.maxPct)}% FTP
            {ftp ? displayDigits(` · ${Math.round((ftp * structureSeg.minPct) / 100)}–${Math.round((ftp * structureSeg.maxPct) / 100)} W`) : ''}
          </div>
          {structureCompliance && structureCompliance !== 'unknown' && (
            <div style={{ fontSize: 8.5, fontWeight: 800, marginTop: 4, color: complianceColor[structureCompliance] }}>
              {complianceLabel[structureCompliance]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sidebar layout primitives ─────────────────────────────────────────────────
function SidebarSectionHeader({ title, icon }: { title: string; icon: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 14px 6px' }}>
      <div style={{ width: 22, height: 22, borderRadius: 7, background: CARD_BG, display: 'grid', placeItems: 'center', color: TEXT_MID, boxShadow: SH_ICON_SM, flexShrink: 0 }}>
        {icon}
      </div>
      <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEXT_MUTED }}>{title}</span>
    </div>
  );
}

function SidebarCard({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div className="analysis-sidebar-card" style={{ margin: '0 10px 10px', borderRadius: 16, background: CARD_BG, padding: '12px 12px 10px', boxShadow: SH_MD, ...style }}>
      {children}
    </div>
  );
}

function MetricIconBox({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div className="analysis-metric-icon" style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, background: `color-mix(in srgb, ${color} 9%, transparent)`, display: 'grid', placeItems: 'center', color }}>
      {children}
    </div>
  );
}

type CoreMetricRow = {
  label: string; unit: string; color: string; icon: React.ReactNode;
  min: string; avg: string; max: string;
};

/** Single Min/Avg/Max header row with one row per sensor (Power, HR, Cadence). */
function CoreMetricsTable({ rows, minLabel, avgLabel, maxLabel, dark = false }: {
  rows: CoreMetricRow[];
  minLabel: string;
  avgLabel: string;
  maxLabel: string;
  dark?: boolean;
}) {
  const colHead: React.CSSProperties = {
    fontSize: 7,
    fontWeight: 800,
    letterSpacing: '0.08em',
    color: dark ? SELECTION_MUTED : TEXT_FAINT,
    textTransform: 'uppercase',
    textAlign: 'center',
    paddingBottom: 4,
  };
  const val: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 800,
    color: dark ? SELECTION_TEXT : TEXT,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.1,
    textAlign: 'center',
  };
  const gridCols = 'minmax(84px, 1.35fr) repeat(3, minmax(0, 1fr))';

  return (
    <div className="analysis-core-metrics" style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '6px 4px', alignItems: 'center' }}>
      <div />
      <div style={colHead}>{minLabel}</div>
      <div style={colHead}>{avgLabel}</div>
      <div style={colHead}>{maxLabel}</div>
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, paddingInlineStart: 2 }}>
            <MetricIconBox color={row.color}>{row.icon}</MetricIconBox>
            <span style={{
              fontSize: 8,
              fontWeight: 800,
              letterSpacing: '0.04em',
              color: dark ? SELECTION_MUTED : TEXT_MUTED,
              textTransform: 'uppercase',
              lineHeight: 1.2,
            }}>
              {row.label}
              <span style={{ fontSize: 7, fontWeight: 700, color: TEXT_FAINT }}> ({row.unit})</span>
            </span>
          </div>
          <div style={val}>{displayDigits(row.min)}</div>
          <div style={{ ...val, color: metricInk(row.color), background: `color-mix(in srgb, ${row.color} 8%, transparent)`, borderRadius: 8, padding: '2px 4px' }}>{displayDigits(row.avg)}</div>
          <div style={val}>{displayDigits(row.max)}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

function MetricTile({ label, value, unit, dark = false, unavailable = false }: {
  label: string; value: string; unit?: string; dark?: boolean; unavailable?: boolean;
}) {
  return (
    <div className="analysis-metric-tile" style={{
      borderRadius: 12,
      background: dark ? SELECTION_SURFACE : BG,
      padding: '16px 8px 9px',
      boxShadow: dark ? 'none' : SH_TILE,
      border: dark ? '1px solid rgba(255,255,255,.06)' : 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
      minHeight: 54,
    }}>
      <span style={{
        fontSize: 9,
        fontWeight: 900,
        letterSpacing: '0.06em',
        color: dark ? SELECTION_TEXT : TEXT,
        textTransform: 'uppercase',
        textAlign: 'center',
        lineHeight: 1.2,
      }}>
        {label}
      </span>
      <span dir={unavailable ? 'auto' : 'ltr'} style={{ fontSize: unavailable ? 10 : 12, fontWeight: 800, color: dark ? SELECTION_TEXT : TEXT, fontVariantNumeric: 'tabular-nums', lineHeight: unavailable ? 1.5 : 1, textAlign: 'center' }}>
        {displayDigits(value)}
        {unit ? <span style={{ fontSize: 8, fontWeight: 700, color: dark ? SELECTION_MUTED : TEXT_MID, marginInlineStart: 2 }}>{unit}</span> : null}
      </span>
    </div>
  );
}

// ── Selection Card ─────────────────────────────────────────────────────────────
function SelectionCard({ stream, selection, apiData, apiLoading, onClear, running }: {
  running: boolean;
  stream: StreamPoint[];
  selection: ChartSelection;
  apiData: any;
  apiLoading: boolean;
  onClear: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [guideMetric, setGuideMetric] = useState<PerformanceGuideMetric | null>(null);
  const localStats = useMemo(() => computeRangeStats(stream, selection.startIdx, selection.endIdx), [stream, selection]);
  const decoupling = useMemo(() => selectionDecoupling(stream, selection.startIdx, selection.endIdx), [stream, selection]);
  const decouplingUnavailableKey = useMemo(() => {
    if (decoupling != null) return null;
    const points = stream.slice(Math.min(selection.startIdx, selection.endIdx), Math.max(selection.startIdx, selection.endIdx) + 1);
    if (!points.some(p => !p.paused && p.heartRate != null && Number.isFinite(p.heartRate) && p.heartRate > 0)) return 'analysis.decouplingNoHeartRate';
    if (!points.some(p => !p.paused && p.power != null && Number.isFinite(p.power) && p.power > 0)) return 'analysis.decouplingNoPower';
    return 'analysis.decouplingInsufficientData';
  }, [decoupling, stream, selection]);
  if (!localStats) return null;

  const src = apiLoading ? {} : (apiData ?? {});
  // Server MMA data (when available) takes precedence over locally-computed values
  const mma = src.min_max_avg ?? src.mma ?? null;
  const hasApi = !!apiData && !apiLoading;

  type StatItem = { label: string; unit: string; min: string; avg: string; max: string; color: string; icon: React.ReactNode };
  const items: StatItem[] = [];

  function addItem(label: string, unit: string, color: string, icon: React.ReactNode,
    apiMin: number | undefined, apiAvg: number | undefined, apiMax: number | undefined,
    local: { min: number; avg: number; max: number } | null | undefined,
    dec = 0) {
    const hasApiVal = apiAvg != null && isFinite(apiAvg);
    const hasLocal = local != null;
    if (!hasApiVal && !hasLocal) return;
    const fmt = (v: number) => dec ? v.toFixed(dec) : String(Math.round(v));
    items.push({
      label, unit, color, icon,
      min: hasApiVal ? fmt(apiMin!) : fmt(local!.min),
      avg: hasApiVal ? fmt(apiAvg!) : fmt(local!.avg),
      max: hasApiVal ? fmt(apiMax!) : fmt(local!.max),
    });
  }

  addItem(t('workoutDetail.map.power'), 'W', BLUE,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
    mma?.power?.min, mma?.power?.avg, mma?.power?.max, localStats.power);
  addItem(t('workoutDetail.map.heartRate'), 'BPM', PINK_TEXT,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    mma?.heart_rate?.min, mma?.heart_rate?.avg, mma?.heart_rate?.max, localStats.heartRate);
  addItem(t(running ? 'runningMetrics.cadence' : 'workoutDetail.map.cadence'), running ? t('runningMetrics.cadenceUnit') : 'rpm', GREEN_D,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}><path d="M21 12a9 9 0 1 1-9-9"/><path d="M21 3v6h-6"/></svg>,
    apiData?.running_metrics?.cadence?.min ?? (stream.some(p => p.cadenceUnit === 'spm') ? localStats.cadence?.min : mma?.cadence?.min),
    apiData?.running_metrics?.cadence?.avg ?? (stream.some(p => p.cadenceUnit === 'spm') ? localStats.cadence?.avg : mma?.cadence?.avg),
    apiData?.running_metrics?.cadence?.max ?? (stream.some(p => p.cadenceUnit === 'spm') ? localStats.cadence?.max : mma?.cadence?.max), localStats.cadence);

  interface MetricChip { key: PerformanceGuideKey; label: string; val: string; unit?: string }
  const extras: MetricChip[] = [];

  function addExtra(key: PerformanceGuideKey, val: unknown, unit: string, dec?: number) {
    if (val == null || val === '') return;
    const v = Number(val);
    if (!isFinite(v)) return;
    const formatted = dec != null ? v.toFixed(dec) : String(Math.round(v));
    extras.push({ key, label: t(`analysis.performanceGuide.metrics.${key}.label`), val: formatted, unit });
  }

  addExtra('if',       src.if ?? src.if_actual,              '', 2);
  addExtra('work',     src.work,                             'kJ', 0);
  addExtra('variability',       src.vi ?? src.vi_actual,              '', 2);
  addExtra('tss',      src.tss ?? src.tss_actual,            '', 1);
  addExtra('normalizedPower',       src.np ?? src.normalizedPower,        'W', 0);
  extras.push({ key: 'pwhr', label: t('analysis.performanceGuide.metrics.pwhr.label'), val: decoupling == null ? t(decouplingUnavailableKey!) : decoupling.toFixed(1), unit: decoupling == null ? '' : '%' });
  addExtra('ef',       src.ef,                               '', 2);
  addExtra('wkg',     src.wkg ?? src.wKg,                   'W/kg', 2);
  addExtra('vam',      src.vam ?? src.vAM,                   'm/h', 0);
  addExtra('calories', src.calories,                         'kcal', 0);
  addExtra('ascent',   src.total_ascent,                     'm', 0);
  addExtra('descent',  src.total_descent,                    'm', 0);
  addExtra('distance', src.distance,                         'km', 2);

  return (
    <div className="analysis-selection-card" dir={i18n.dir()} style={{
      margin: '8px 10px 10px',
      borderRadius: 16,
      background: SELECTION_BG,
      padding: '12px 10px 10px',
      boxShadow: '0 4px 22px rgba(26,31,43,.32)',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '0 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={SELECTION_MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
          <span style={{ fontSize: 10, fontWeight: 700, color: SELECTION_TEXT, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', direction: 'ltr' }}>
            {secToHMS(selection.timeStartSec ?? Math.min(localStats.timeStart, localStats.timeEnd))}
            {' – '}
            {secToHMS(selection.timeEndSec ?? Math.max(localStats.timeStart, localStats.timeEnd))}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {apiLoading && (
            <NmLoader size="xs" inline />
          )}
          {hasApi && (
            <span style={{ fontSize: 8, fontWeight: 800, color: metricInk(GREEN_D), letterSpacing: '0.04em', padding: '3px 7px', borderRadius: 20, background: 'rgba(60,198,125,.18)' }}>{t('analysis.selectionCalculated')} ✓</span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={PINK} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
            <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: metricInk(PINK) }}>{t('workoutDetail.map.selectedRange')}</span>
          </div>
          <ActionIconButton
            onClick={onClear}
            type="button"
            title={t('workoutDetail.map.clearSelection')}
            label={t('workoutDetail.map.clearSelection')}
            icon={<svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>}
          />
        </div>
      </div>

      {/* Duration hero card */}
      <div style={{ margin: '0 0 8px', borderRadius: 12, background: SELECTION_SURFACE, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', border: '1px solid rgba(255,255,255,.06)' }}>
        <div>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: SELECTION_MUTED, marginBottom: 4 }}>{t('analysis.selectionDuration')}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: SELECTION_TEXT, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', lineHeight: 1 }}>{secToHMS(selection.durationSec ?? localStats.duration)}</div>
        </div>
        <MetricIconBox color={BLUE}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
            <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
          </svg>
        </MetricIconBox>
      </div>

      {running && <RunningMetricsPanel
        variant="selection"
        metrics={runningSelectionMetrics(src, localStats, stream.some(point => point.cadenceUnit === 'spm'))}
      />}
      {/* Cycling selection metrics */}
      {!running && items.length > 0 && (
        <div style={{ margin: '0 0 8px', borderRadius: 12, background: SELECTION_SURFACE, padding: '10px 8px 10px', border: '1px solid rgba(255,255,255,.06)' }}>
          <CoreMetricsTable
            rows={items}
            minLabel={t('workoutDetail.summary.min')}
            avgLabel={t('workoutDetail.summary.avg')}
            maxLabel={t('workoutDetail.summary.max')}
            dark
          />
        </div>
      )}

      {/* Metric tile grid */}
      {!running && extras.length > 0 && (
        <div className="analysis-selection-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 2 }}>
          {extras.map((s) => (
            <button key={s.key} type="button" onClick={() => setGuideMetric({ ...s, color: metricInk(BLUE), iconPath: 'M12 16v-4M12 8h.01' })}
              aria-label={`${s.label}: ${s.val} ${s.unit ?? ''} — ${t('analysis.performanceGuide.eyebrow')}`}
              style={{ position: 'relative', padding: 0, border: 0, borderRadius: 12, background: 'transparent', font: 'inherit', cursor: 'pointer', minWidth: 0 }}>
              <span aria-hidden="true" style={{ position: 'absolute', insetInlineEnd: 5, top: 3, color: metricInk(BLUE), fontSize: 11 }}>ⓘ</span>
              <MetricTile label={s.label} value={s.val} unit={s.unit} dark unavailable={s.key === 'pwhr' && decoupling == null} />
            </button>
          ))}
        </div>
      )}
      <PerformanceGuideModal metric={guideMetric} onClose={() => setGuideMetric(null)} />
    </div>
  );
}

// ── Right panel section components ────────────────────────────────────────────
function RSection({ title, children, icon }: { title?: string; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div style={{ paddingBottom: 4 }}>
      {title && (icon ? (
        <SidebarSectionHeader title={title} icon={icon} />
      ) : (
        <div style={{ padding: '12px 14px 6px', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEXT_MUTED }}>{title}</div>
      ))}
      <div style={{ padding: '0 0 4px' }}>{children}</div>
    </div>
  );
}

const ICON_CHART = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
    <path d="M18 20V10M12 20V4M6 20v-6"/>
  </svg>
);
const ICON_WAVE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
    <path d="M22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

function HeroTrio({ wo, total }: { wo: WorkoutDetail; total?: any }) {
  const { t } = useTranslation();
  const src = isRunningWorkout(wo) ? wo : total ?? wo;
  const tss = src.tss_actual != null ? String(Math.round(Number(src.tss_actual))) : '—';
  const time = src.total_time != null ? secToHMS(Number(src.total_time)) : '—';
  const dist = src.distance != null ? (Number(src.distance) / 1000).toFixed(2) : '—';

  return (
    <SidebarCard style={{ marginTop: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, textAlign: 'center' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
            <MetricIconBox color={PURPLE}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ width: 11, height: 11 }}><path d="M3 3v18h18"/></svg>
            </MetricIconBox>
            <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.06em', color: TEXT_MUTED }}>{workoutStressLabel(wo)}</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: metricInk(PURPLE), fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{displayDigits(tss)}</div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
            <MetricIconBox color={BLUE}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ width: 11, height: 11 }}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
            </MetricIconBox>
            <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.06em', color: TEXT_MUTED, textTransform: 'uppercase' }}>{t('workoutDetail.metrics.time')}</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: TEXT, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{displayDigits(time)}</div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
            <MetricIconBox color={GREEN_D}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ width: 11, height: 11 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </MetricIconBox>
            <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.06em', color: TEXT_MUTED, textTransform: 'uppercase' }}>{t('workoutDetail.metrics.distance')}</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: TEXT, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            {displayDigits(dist)}<span style={{ fontSize: 10, fontWeight: 700, color: TEXT_FAINT, marginLeft: 2 }}>km</span>
          </div>
        </div>
      </div>
    </SidebarCard>
  );
}

function PerformanceRow({ iconPath, color, label, val, unit, onGuide }: { iconPath: string; color: string; label: string; val: string; unit?: string; onGuide: () => void }) {
  return (
    <button type="button" className="performance-metric-guide__trigger" onClick={onGuide} aria-label={`${label}: ${displayDigits(val)}${unit ? ` ${unit}` : ''}`}>
      <MetricIconBox color={color}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10 }}><path d={iconPath}/></svg>
      </MetricIconBox>
      <span style={{ flex: 1, fontSize: 9, fontWeight: 700, color: TEXT_MID }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 800, color: TEXT, fontVariantNumeric: 'tabular-nums' }}>
        {displayDigits(val)}{unit ? <span style={{ fontSize: 8, fontWeight: 700, color: TEXT_FAINT, marginLeft: 2 }}>{unit}</span> : null}
      </span>
    </button>
  );
}

function StatsGrid({ wo, total }: { wo: WorkoutDetail; total?: any }) {
  const { t } = useTranslation();
  const [guideMetric, setGuideMetric] = useState<PerformanceGuideMetric | null>(null);
  const src = total ?? wo;
  type Row = PerformanceGuideMetric & { raw: unknown; dec?: number };
  const left: Row[] = [];
  const right: Row[] = [];
  const add = (side: 'left' | 'right', key: PerformanceGuideKey, iconPath: string, color: string, raw: unknown, unit?: string, dec = 2) => {
    if (raw == null || raw === '') return;
    const v = Number(raw);
    const val = isFinite(v) ? (dec === 0 ? String(Math.round(v)) : v.toFixed(dec)) : String(raw);
    (side === 'left' ? left : right).push({ key, iconPath, color, label: t(`analysis.performanceGuide.metrics.${key}.label`), val, raw: val, unit, dec });
  };

  add('left',  'work',           'M12 20V10M18 20V4M6 20v-4',                       ORANGE,    src.work,           'kJ', 0);
  add('left',  'variability',    'M22 12 18 12 15 21 9 3 6 12 2 12',               TEXT_MID,  src.vi,             undefined, 2);
  add('left',  'ef',             'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z', GREEN_D, src.ef, undefined, 2);
  add('left',  'ascent',         'M18 15l-6-6-6 6',                                 GREEN_D,   src.total_ascent,   'm', 0);
  add('left',  'vam',            'M5 3l14 9-14 9V3z',                               PINK_TEXT, src.vam ?? src.vAM, 'm/h', 2);
  add('right', 'if',             'M13 2L3 14h9l-1 8 10-12h-9l1-8z',                BLUE,      src.if_actual,      undefined, 2);
  add('right', 'normalizedPower','M13 2L3 14h9l-1 8 10-12h-9l1-8z',                PINK_TEXT, src.np,             'W', 0);
  add('right', 'pwhr',           'M4 12h16M12 4v16',                                TEXT_MID,  src.pwhr,           '%', 1);
  add('right', 'wkg',            'M3 3h18M3 12h18M3 21h18',                         GOLD,      src.wkg,            'W/kg', 2);
  add('right', 'descent',        'M6 9l6 6 6-6',                                    PINK,      src.total_descent,  'm', 0);

  const calories = src.calories != null && isFinite(Number(src.calories)) ? String(Math.round(Number(src.calories))) : null;
  if (!left.length && !right.length && !calories) return null;

  return (
    <SidebarCard style={{ marginTop: 0, padding: '8px 12px 10px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
        <div>
          {left.map((c, i) => (
            <PerformanceRow key={i} iconPath={c.iconPath} color={c.color} label={c.label} val={c.val} unit={c.unit} onGuide={() => setGuideMetric(c)} />
          ))}
        </div>
        <div>
          {right.map((c, i) => (
            <PerformanceRow key={i} iconPath={c.iconPath} color={c.color} label={c.label} val={c.val} unit={c.unit} onGuide={() => setGuideMetric(c)} />
          ))}
        </div>
      </div>
      {calories && (
        <button type="button" className="performance-metric-guide__trigger performance-metric-guide__trigger--calories" onClick={() => setGuideMetric({ key: 'calories', iconPath: 'M12 2c-5.523 0-10 4.477-10 10s4.477 10 10 10', color: metricInk(ORANGE), label: t('analysis.performanceGuide.metrics.calories.label'), val: calories, unit: 'kcal' })}>
          <MetricIconBox color={ORANGE}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ width: 10, height: 10 }}><path d="M12 2c-5.523 0-10 4.477-10 10s4.477 10 10 10"/></svg>
          </MetricIconBox>
          <span style={{ fontSize: 9, fontWeight: 700, color: TEXT_MID }}>{t('analysis.performanceGuide.metrics.calories.label')}</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: TEXT, fontVariantNumeric: 'tabular-nums' }}>{displayDigits(calories)}<span style={{ fontSize: 8, fontWeight: 700, color: TEXT_FAINT, marginLeft: 2 }}>kcal</span></span>
        </button>
      )}
      <PerformanceGuideModal metric={guideMetric} onClose={() => setGuideMetric(null)} />
    </SidebarCard>
  );
}

export function MamTable({ mma, running = false }: { mma: any; running?: boolean }) {
  const { t } = useTranslation();
  if (!mma) return null;
  type MamRow = { label: string; unit: string; color: string; iconPath: string; min: string; avg: string; max: string };
  const rows: MamRow[] = [];
  const add = (key: string, label: string, unit: string, color: string, iconPath: string, dec = 0) => {
    if (!mma[key]) return;
    rows.push({
      label, unit, color, iconPath,
      min: fmtNum(mma[key].min, dec),
      avg: fmtNum(mma[key].avg, dec),
      max: fmtNum(mma[key].max, dec),
    });
  };
  add('power',      t('workoutDetail.summary.stat.power'), t('analysis.sensorUnits.power'),    BLUE,      'M13 2L3 14h9l-1 8 10-12h-9l1-8z');
  add('heart_rate', t('workoutDetail.summary.stat.heart_rate'), t('analysis.sensorUnits.heartRate'),  PINK_TEXT, 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z');
  add('cadence',    t('workoutDetail.summary.stat.cadence'), t(running ? 'runningMetrics.cadenceUnit' : 'analysis.sensorUnits.cadence'),  GREEN_D,   'M21 12a9 9 0 1 1-9-9');
  add('speed',      t('workoutDetail.summary.stat.speed'), t('analysis.sensorUnits.speed'), BLUE,      'M13 2L3 14h9l-1 8 10-12h-9l1-8z', 1);
  add('altitude', t('workoutDetail.map.seriesElevation'), 'm', GREEN_D, 'M3 20l7-14 4 8 3-5 4 11H3z', 1);
  add('temperature', t('runningMetrics.temperature'), '°C', PURPLE, 'M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0z', 1);
  if (mma.pace) rows.push({
    label: t('runningMetrics.paceLabel'), unit: t('runningMetrics.paceUnit'), color: BLUE,
    iconPath: 'M12 8v4l3 3M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0',
    min: displayDigits(formatRunningPace(mma.pace.min)), avg: displayDigits(formatRunningPace(mma.pace.avg)), max: displayDigits(formatRunningPace(mma.pace.max)),
  });
  if (!rows.length) return null;

  const th: React.CSSProperties = { padding: '8px 4px', fontSize: 8, fontWeight: 800, textAlign: 'center', letterSpacing: '0.08em', textTransform: 'uppercase' };
  const td: React.CSSProperties = { padding: '9px 4px', textAlign: 'center', fontSize: 14, fontWeight: 800, fontVariantNumeric: 'tabular-nums' };

  return (
    <SidebarCard style={{ marginTop: 0, padding: '6px 8px 8px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(163,177,198,.18)' }}>
            <th style={{ ...th, textAlign: 'start', width: 72 }}/>
            <th style={{ ...th, color: metricInk(PURPLE) }}>{t('workoutDetail.summary.max')}</th>
            <th style={{ ...th, color: metricInk(BLUE) }}>{t('workoutDetail.summary.avg')}</th>
            <th style={{ ...th, color: metricInk(GREEN_D) }}>{t('workoutDetail.summary.min')}</th>
            <th style={{ ...th, color: TEXT_FAINT, width: 36 }}/>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const b = i < rows.length - 1 ? '1px solid rgba(163,177,198,.1)' : 'none';
            return (
              <tr key={r.label}>
                <td style={{ ...td, textAlign: 'start', fontSize: 8, fontWeight: 800, color: TEXT_MUTED, letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: b, paddingInlineStart: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <MetricIconBox color={r.color}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ width: 9, height: 9 }}><path d={r.iconPath}/></svg>
                    </MetricIconBox>
                    {r.label}
                  </div>
                </td>
                <td style={{ ...td, color: TEXT, borderBottom: b }} dir="ltr">{r.max}</td>
                <td style={{ ...td, color: TEXT, borderBottom: b }} dir="ltr">{r.avg}</td>
                <td style={{ ...td, color: TEXT, borderBottom: b }} dir="ltr">{r.min}</td>
                <td dir="auto" style={{ ...td, fontSize: 8, fontWeight: 700, color: TEXT_FAINT, borderBottom: b }}>{r.unit}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SidebarCard>
  );
}


function PeakBars({
  peakData,
  title,
  unit,
  color,
  dec = 0,
  stream,
  streamReady = false,
  activeKey = null,
  onPeakSelect,
}: {
  peakData: any;
  title: string;
  unit: string;
  color: string;
  dec?: number;
  stream?: StreamPoint[];
  streamReady?: boolean;
  activeKey?: string | null;
  onPeakSelect?: (entry: Record<string, unknown>, key: string) => void;
}) {
  if (!peakData) return null;
  const entries = (Object.values(peakData) as any[]).filter(e => e && isFinite(Number(e.value))).sort((a, b) => a.interval - b.interval);
  if (!entries.length) return null;
  const maxV = Math.max(...entries.map(e => Number(e.value)));
  const canSelect = streamReady && (stream?.length ?? 0) > 0 && !!onPeakSelect;
  return (
    <div style={{ borderRadius: 13, background: BG, overflow: 'hidden', boxShadow: SH_MD }}>
      <div style={{ padding: '10px 12px 6px', fontSize: 11, fontWeight: 800, color: TEXT }}>{title}</div>
      <div style={{ padding: '0 12px 10px', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {entries.map((e, i) => {
          const pct = maxV > 0 ? (Number(e.value) / maxV) * 100 : 0;
          const val = dec ? Number(e.value).toFixed(dec) : String(Math.round(Number(e.value)));
          const rowKey = `${title}-${e.label ?? e.interval ?? i}`;
          const isActive = activeKey === rowKey;
          const clickable = canSelect && findStreamIndicesForModalItem(stream!, e) != null;
          return (
            <div
              key={i}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onPeakSelect!(e, rowKey) : undefined}
              onKeyDown={clickable ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onPeakSelect!(e, rowKey); } } : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 0',
                borderBottom: i < entries.length - 1 ? '1px solid rgba(163,177,198,.1)' : 'none',
                cursor: clickable ? 'pointer' : 'default',
                borderRadius: 6,
                background: isActive ? 'rgba(74,144,217,.12)' : 'transparent',
                opacity: canSelect && !clickable ? 0.45 : 1,
              }}
            >
              <span style={{ width: 40, fontSize: 9, fontWeight: 800, color: DETAIL_LABEL, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{displayDigits(e.label ?? '')}</span>
              <div style={{ flex: 1, height: 10, background: SUNKEN, borderRadius: 3, overflow: 'hidden', boxShadow: SH_IN_SM }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }}/>
              </div>
              <span style={{ width: 58, fontSize: 11, fontWeight: 700, color: DETAIL_VALUE, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {displayDigits(val)} <span style={{ fontSize: 8, fontWeight: 600, color: TEXT_FAINT }}>{unit}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LapsTable({
  laps,
  stream,
  streamReady = false,
  workoutDurationSec,
  activeLapIndex = null,
  onLapSelect,
}: {
  laps: any[];
  stream?: StreamPoint[];
  streamReady?: boolean;
  workoutDurationSec: number;
  activeLapIndex?: number | null;
  onLapSelect?: (lapIndex: number) => void;
}) {
  const { t } = useTranslation();
  if (!laps.length) return <div style={{ padding: '24px 14px', textAlign: 'center', color: TEXT_MUTED, fontSize: 12 }}>No laps</div>;
  type Col = { key: string; label: string; fmt: (l: any) => string; color?: string };
  const cols: Col[] = [
    { key: 'lap',      label: '#',     fmt: l => String((l.lap ?? 0) + 1) },
    { key: 'time',     label: t('workoutDetail.metrics.time'),  fmt: l => l.total_elapsed_time ? secToHMS(Math.round(l.total_elapsed_time)) : '—' },
    { key: 'dist',     label: 'km',    fmt: l => l.total_distance != null ? Number(l.total_distance).toFixed(1) : '—' },
    { key: 'avgHR',    label: t('workoutDetail.map.seriesHr'),    fmt: l => l.avg_heart_rate != null ? String(l.avg_heart_rate) : '—', color: metricInk(PINK) },
    { key: 'avgPwr',   label: t('workoutDetail.map.seriesPower'),     fmt: l => l.avg_power != null ? String(l.avg_power) : '—', color: PW_Z_DOTS[3] },
    { key: 'pace', label: 'min/km', fmt: l => formatRunningPace(l.running_metrics?.avg_pace_seconds_per_km) },
    { key: 'cadenceSpm', label: `${t('runningMetrics.cadence')} (${t('runningMetrics.cadenceUnit')})`, fmt: l => l.running_metrics?.cadence?.avg != null ? Number(l.running_metrics.cadence.avg).toFixed(1) : '—' },
    { key: 'rtss', label: 'rTss', fmt: l => l.running_metrics?.rtss != null ? String(l.running_metrics.rtss) : '—' },
    { key: 'ascent',   label: '↑m',    fmt: l => l.total_ascent != null ? String(l.total_ascent) : '—', color: metricInk(GREEN_D) },
  ];
  const activeCols = cols.filter(c => laps.some(l => c.fmt(l) !== '—'));
  const thS: React.CSSProperties = { padding: '7px 9px', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.07em', color: DETAIL_LABEL, background: SUNKEN, textAlign: 'center', textTransform: 'uppercase', borderBottom: '1px solid rgba(163,177,198,.2)', whiteSpace: 'nowrap' };
  const tdS: React.CSSProperties = { padding: '13px 9px', textAlign: 'center', fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: TEXT };
  const canSelect = streamReady && (stream?.length ?? 0) > 0 && !!onLapSelect;
  return (
    <div className="analysis-laps">
      <div style={{ overflowX: 'auto', scrollbarWidth: 'none' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 260 }}>
          <thead>
            <tr>{activeCols.map(c => <th key={c.key} style={{ ...thS, textAlign: c.key === 'lap' ? 'left' : 'center' }}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {laps.map((lap: any, i: number) => {
              const clickable = canSelect && findStreamIndicesForLap(laps as LapRow[], i, stream!, workoutDurationSec) != null;
              const isActive = activeLapIndex === i;
              return (
              <tr
                key={i}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => onLapSelect!(i) : undefined}
                onKeyDown={clickable ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onLapSelect!(i); } } : undefined}
                style={{
                  cursor: clickable ? 'pointer' : 'default',
                  background: isActive ? 'rgba(74,144,217,.12)' : 'transparent',
                  opacity: canSelect && !clickable ? 0.45 : 1,
                }}
              >
                {activeCols.map(c => (
                  <td key={c.key} style={{ ...tdS, textAlign: c.key === 'lap' ? 'left' : 'center', fontWeight: 700, color: TEXT, borderBottom: i < laps.length-1 ? '1px solid rgba(163,177,198,.1)' : 'none' }}>
                    {displayDigits(c.fmt(lap))}
                  </td>
                ))}
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Top Bar ───────────────────────────────────────────────────────────────────
function TopBar({ workout, loading, onClose, onTitleUpdated, onShare }: { onShare: () => void; workout: WorkoutDetail; loading: boolean; onClose: () => void; onTitleUpdated?: (title: string) => void }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const state = calendarWorkoutState(workout, localCalendarDay());
  const statusColor = state.done ? '#16a34a' : state.missed ? '#dc2626' : 'var(--studio-muted)';
  const initialTitle = workout.title ?? workout.workout_type?.name ?? '';
  const [title, setTitle] = useState(initialTitle);
  const [titleDraft, setTitleDraft] = useState(initialTitle);
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleSaving, setTitleSaving] = useState(false);
  const [titleSaveFailed, setTitleSaveFailed] = useState(false);

  useEffect(() => {
    const nextTitle = workout.title ?? workout.workout_type?.name ?? '';
    setTitle(nextTitle);
    if (!titleEditing) setTitleDraft(nextTitle);
  }, [titleEditing, workout.id, workout.title, workout.workout_type?.name]);

  const commitTitle = useCallback(async () => {
    if (titleSaving) return;
    const nextTitle = titleDraft.trim();
    setTitleEditing(false);
    if (!nextTitle || nextTitle === title) { setTitleDraft(title); return; }
    setTitleSaving(true);
    setTitleSaveFailed(false);
    try {
      await workouts.updateWorkout(workout.id, { title: nextTitle });
      setTitle(nextTitle);
      setTitleDraft(nextTitle);
      onTitleUpdated?.(nextTitle);
    } catch {
      setTitleDraft(title);
      setTitleSaveFailed(true);
    } finally { setTitleSaving(false); }
  }, [onTitleUpdated, title, titleDraft, titleSaving, workout.id]);
  const chips = [
    isRunningWorkout(workout) ? { val: formatRunningPace(workout.running_metrics?.avg_pace_seconds_per_km ?? (workout.distance && workout.total_time ? workout.total_time * 1000 / workout.distance : null)), unit: 'min/km' } : null,
    workout.total_time   != null ? { val: secToHMS(Number(workout.total_time)),                         unit: ''    } : null,
    workout.distance     != null ? { val: (Number(workout.distance) / 1000).toFixed(2),                  unit: 'km'  } : null,
    workout.tss_actual   != null ? { val: String(Math.round(Number(workout.tss_actual))),                 unit: workoutStressLabel(workout) } : null,
    workout.total_ascent != null ? { val: String(workout.total_ascent),                                  unit: 'm↑'  } : null,
    !isRunningWorkout(workout) && workout.wkg != null ? { val: Number(workout.wkg).toFixed(2),                                unit: 'W/kg'} : null,
  ].filter(Boolean) as { val: string; unit: string }[];

  return (
    <header
      className="title-bar-no-drag analysis-header"
      data-workout-status={state.done ? 'completed' : state.missed ? 'missed' : 'pending'}
      style={{ flexShrink: 0, minHeight: 62, background: BG, borderBottom: '1px solid rgba(163,177,198,.28)', boxShadow: '0 2px 16px var(--nm-dark)', display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px', position: 'relative', zIndex: 10 }}
    >
      <ActionButton
        type="button"
        className="analysis-close"
        aria-label={t('analysis.close')}
        onClick={onClose}
      ><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        <span>{t('analysis.close')}</span></ActionButton>

      <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: statusColor, display: 'grid', placeItems: 'center', color: '#fff', boxShadow: 'none' }}>
        <WorkoutTypeIcon name={workout.workout_type?.english_name ?? ''} size={18} />
      </div>

      <div style={{ flex: '0 1 32%', minWidth: 0 }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', color: TEXT_MUTED, textTransform: 'uppercase', marginBottom: 2 }}>{displayDigits(workout.workout_day)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', overflow: 'hidden' }}>
          {titleEditing ? (
            <input autoFocus value={titleDraft} disabled={titleSaving} aria-label={t('workoutDetail.actions.edit')} onChange={(event) => setTitleDraft(event.target.value)} onBlur={() => void commitTitle()} onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); void commitTitle(); }
              else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setTitleDraft(title); setTitleEditing(false); }
            }} style={{ minWidth: 0, width: 'min(340px, 38vw)', height: 30, padding: '0 9px', borderRadius: 8, border: `1px solid ${PINK}`, outline: 'none', background: BG, color: TEXT, fontFamily: 'inherit', fontSize: 17, fontWeight: 800, boxShadow: SH_IN_SM }} />
          ) : (
            <button type="button" disabled={titleSaving} onClick={() => { setTitleSaveFailed(false); setTitleEditing(true); }} title={titleSaveFailed ? t('workoutForm.error.updateFailed') : t('workoutDetail.actions.edit')} style={{ minWidth: 0, maxWidth: '38vw', padding: '3px 6px', borderRadius: 7, border: titleSaveFailed ? '1px solid #ef4444' : '1px solid transparent', background: 'transparent', color: TEXT, fontFamily: 'inherit', fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', cursor: 'text', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayDigits(title)}</button>
          )}

        </div>
      </div>

          {chips.length > 0 && (
            <div className="analysis-header-metrics" style={{ display: 'flex', gap: 14, alignItems: 'center', justifyContent: isRtl ? 'flex-end' : 'flex-start', flex: 1, flexWrap: 'wrap', minWidth: 0, paddingBlock: 10 }}>
              {chips.map((c, i) => (
                <React.Fragment key={i}>
                  <div style={{ width: 1, height: 26, background: 'rgba(163,177,198,.4)' }}/>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                    <span style={{ fontSize: 26, lineHeight: 1.3, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: TEXT }}>{displayDigits(c.val)}</span>
                    {c.unit && <span style={{ fontSize: 12, fontWeight: 700, color: TEXT_MUTED }}>{c.unit}</span>}
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

      <ActionButton
        type="button"
        className="analysis-close"
        onClick={onShare}
      >{t('workoutShare.title')}</ActionButton>
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NmLoader size="xs" inline label={t('analysis.loading')} />
        </div>
      )}


    </header>
  );
}

// ── Draggable chart panel layout ─────────────────────────────────────────────

type ChartPanelId = 'map' | 'pdc' | 'stream';

const PANEL_ORDER_KEY = 'vo2-bridge-analysis-panel-order';
const MAP_HEIGHT_KEY = 'vo2-bridge-analysis-map-height';
const DEFAULT_PANEL_ORDER: ChartPanelId[] = ['map', 'stream', 'pdc'];
const DEFAULT_MAP_HEIGHT = 200;
const MIN_MAP_HEIGHT = 100;
const MAX_MAP_HEIGHT = 520;

function clampMapHeight(h: number): number {
  return Math.min(MAX_MAP_HEIGHT, Math.max(MIN_MAP_HEIGHT, Math.round(h)));
}

function loadMapHeight(): number {
  try {
    const raw = localStorage.getItem(MAP_HEIGHT_KEY);
    if (!raw) return DEFAULT_MAP_HEIGHT;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_MAP_HEIGHT;
    return clampMapHeight(n);
  } catch {
    return DEFAULT_MAP_HEIGHT;
  }
}

/** Horizontal grip — drag vertically to resize the map panel. */
function MapHeightResizeHandle({ height, onResize, onResizeEnd }: {
  height: number;
  onResize: (height: number) => void;
  onResizeEnd: (height: number) => void;
}) {
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const startY = e.clientY;
    const startHeight = height;
    attachWindowPointerDrag(
      e,
      (_clientX, clientY) => {
        onResize(clampMapHeight(startHeight + (clientY - startY)));
      },
      (_clientX, clientY) => {
        onResizeEnd(clampMapHeight(startHeight + (clientY - startY)));
      },
    );
  }, [height, onResize, onResizeEnd]);

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-valuenow={height}
      aria-valuemin={MIN_MAP_HEIGHT}
      aria-valuemax={MAX_MAP_HEIGHT}
      title="Drag to resize map"
      className="chart-touch-handle"
      onPointerDown={onPointerDown}
      style={{
        flexShrink: 0,
        height: 14,
        touchAction: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(163,177,198,.14)',
        borderTop: '1px solid rgba(163,177,198,.22)',
        borderBottom: '1px solid rgba(163,177,198,.18)',
      }}
    >
      <div style={{
        width: 36,
        height: 3,
        borderRadius: 2,
        background: 'rgba(107,114,130,.45)',
        boxShadow: 'inset 0 1px 2px rgba(255,255,255,.35)',
      }} />
    </div>
  );
}

function panelLabels(t: (key: string) => string): Record<ChartPanelId, string> {
  return {
    map: t('analysis.panel.routeMap'),
    pdc: t('analysis.panel.pdc'),
    stream: t('analysis.panel.stream'),
  };
}

function loadPanelOrder(): ChartPanelId[] {
  try {
    const raw = localStorage.getItem(PANEL_ORDER_KEY);
    if (!raw) return DEFAULT_PANEL_ORDER;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return DEFAULT_PANEL_ORDER;
    const ids = parsed.filter((id): id is ChartPanelId =>
      id === 'map' || id === 'pdc' || id === 'stream',
    );
    if (ids.length !== 3 || new Set(ids).size !== 3) return DEFAULT_PANEL_ORDER;
    // Migrate legacy order (pdc between map and stream) → stream under map, pdc below
    if (ids.indexOf('pdc') < ids.indexOf('stream')) return DEFAULT_PANEL_ORDER;
    return ids;
  } catch {
    return DEFAULT_PANEL_ORDER;
  }
}

function reorderPanels(order: ChartPanelId[], from: ChartPanelId, to: ChartPanelId): ChartPanelId[] {
  if (from === to) return order;
  const next = order.filter(id => id !== from);
  const toIdx = next.indexOf(to);
  if (toIdx === -1) return order;
  next.splice(toIdx, 0, from);
  return next;
}

function ChartPanelSlot({
  id,
  label,
  flex,
  isOver,
  isDragging,
  slotAttr,
  onBeginReorder,
  children,
}: {
  id: ChartPanelId;
  label: string;
  flex?: boolean;
  isOver: boolean;
  isDragging: boolean;
  slotAttr: string;
  onBeginReorder: (id: ChartPanelId, e: React.PointerEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <div
      {...{ [slotAttr]: id }}
      style={{
        flex: flex ? '1 1 0' : '0 0 auto',
        minHeight: flex ? 0 : undefined,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        opacity: isDragging ? 0.55 : 1,
        boxShadow: isOver ? `inset 0 3px 0 ${PINK}` : undefined,
        transition: 'opacity 120ms, box-shadow 120ms',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={`Reorder ${label} panel`}
        className="chart-touch-handle chart-panel-reorder-handle"
        onPointerDown={(e) => onBeginReorder(id, e)}
        style={{
          flexShrink: 0,
          minHeight: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 12px',
          touchAction: 'none',
          userSelect: 'none',
          background: isDragging ? 'color-mix(in srgb, var(--text-accent) 12%, transparent)' : 'rgba(163,177,198,.12)',
          borderBottom: '1px solid rgba(163,177,198,.18)',
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: TEXT_MUTED,
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
          <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
          <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
        </svg>
        {label}
      </div>
      <div style={{
        flex: flex ? 1 : undefined,
        minHeight: flex ? 0 : undefined,
        overflow: flex ? 'hidden' : 'visible',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {children}
      </div>
    </div>
  );
}

// ── WorkoutAnalysisView ───────────────────────────────────────────────────────
export function WorkoutAnalysisView({ workout, userId, onClose, onTitleUpdated }: { workout: WorkoutDetail; userId?: number; onClose: () => void; onTitleUpdated?: (title: string) => void }) {
  const { t, i18n } = useTranslation();
  const { ftp, zoneSettings, loading: zonesLoading } = useAthleteMetrics(userId ?? null);
  const comparisonNav = useComparisonNavigation();
  const [details, setDetails]       = useState<any>(null);
  const [loading, setLoading]       = useState(true);
  const [stream, setStream]         = useState<StreamPoint[]>([]);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamError, setStreamError]     = useState<string | null>(null);
  const statsPanelRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection]   = useState<ChartSelection | null>(null);
  const [sidebarHighlightKey, setSidebarHighlightKey] = useState<string | null>(null);
  const [activeLapIndex, setActiveLapIndex] = useState<number | null>(null);
  const [summaryTab, setSummaryTab] = useState<'performance' | 'mam'>('performance');
  const [activePeakKey, setActivePeakKey] = useState(isRunningWorkout(workout) ? 'sd_peak' : 'pw_peak');
  useEffect(() => {
    if (!selection) return;
    statsPanelRef.current?.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [selection]);
  const [rangeApiData, setRangeApiData]     = useState<any>(null);
  const [rangeApiLoading, setRangeApiLoading] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const rangeRequestId = useRef(0);
  useEffect(() => () => { rangeRequestId.current += 1; }, [workout.id]);

  const fetchRangeForSelection = useCallback((sel: ChartSelection) => {
    const requestId = ++rangeRequestId.current;
    if (!userId) {
      setRangeApiData(null);
      setRangeApiLoading(false);
      return;
    }

    const startTs = stream[sel.startIdx]?.timestamp;
    const endTs = stream[sel.endIdx]?.timestamp;
    if (startTs == null || endTs == null) {
      setRangeApiData(null);
      setRangeApiLoading(false);
      return;
    }

    setRangeApiLoading(true);
    setRangeApiData(null);
    workouts.calculateSelectedRange(workout.id, userId, startTs, endTs)
      .then(res => {
        if (requestId !== rangeRequestId.current) return;
        const calc = res.data?.data?.calculation ?? res.data?.calculation ?? res.data ?? null;
        setRangeApiData(calc);
      })
      .catch(() => { if (requestId === rangeRequestId.current) setRangeApiData(null); })
      .finally(() => { if (requestId === rangeRequestId.current) setRangeApiLoading(false); });
  }, [stream, workout.id, userId]);

  /** Chart drag-select applies immediately — no confirm step. */
  const handleSelectionChange = useCallback((sel: ChartSelection | null) => {
    setSelection(sel);
    if (!sel) {
      rangeRequestId.current += 1;
      setRangeApiData(null);
      setRangeApiLoading(false);
      setSidebarHighlightKey(null);
      setActiveLapIndex(null);
    } else {
      setSidebarHighlightKey(null);
      setActiveLapIndex(null);
      fetchRangeForSelection(sel);
    }
  }, [fetchRangeForSelection]);

  const defaultRange = useMemo(
    () => defaultCompareRange(workout.workout_day ?? ''),
    [workout.workout_day],
  );
  const [pdcPeakType, setPdcPeakType]       = useState<PeakType>('pw_peak');
  const [compareStart, setCompareStart]     = useState(defaultRange.start);
  const [compareEnd, setCompareEnd]         = useState(defaultRange.end);
  const [compareCurve, setCompareCurve]     = useState<PdcPoint[]>([]);
  const [pdcLoading, setPdcLoading]         = useState(false);
  const [panelOrder, setPanelOrder]         = useState<ChartPanelId[]>(loadPanelOrder);
  const [mapHeight, setMapHeight]           = useState(loadMapHeight);

  const persistPanelOrder = useCallback((order: ChartPanelId[]) => {
    setPanelOrder(order);
    try { localStorage.setItem(PANEL_ORDER_KEY, JSON.stringify(order)); } catch { /* ignore */ }
  }, []);

  const handleReorderPanel = useCallback((from: ChartPanelId, to: ChartPanelId) => {
    persistPanelOrder(reorderPanels(panelOrder, from, to));
  }, [panelOrder, persistPanelOrder]);

  const {
    dragId: dragPanelId,
    overId: dropOverPanelId,
    beginReorder: beginPanelReorder,
    slotAttr: panelSlotAttr,
  } = usePointerPanelReorder<ChartPanelId>(handleReorderPanel);

  const persistMapHeight = useCallback((height: number) => {
    const clamped = clampMapHeight(height);
    setMapHeight(clamped);
    try { localStorage.setItem(MAP_HEIGHT_KEY, String(clamped)); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    setCompareStart(defaultRange.start);
    setCompareEnd(defaultRange.end);
  }, [defaultRange.start, defaultRange.end]);

  const loadDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await workouts.getWorkoutDetails(workout.id);
      const data = res.data?.data?.workoutDetails ?? res.data?.workoutDetails ?? res.data?.data ?? res.data;
      setDetails(data);
    } catch { /* fall through to show what we have */ } finally { setLoading(false); }
  }, [workout.id]);

  // Stream lives on GET /workout/{id}/data — NOT on /details
  const loadStream = useCallback(async () => {
    setStreamLoading(true);
    setStreamError(null);
    try {
      const res = await workouts.getWorkoutData(workout.id);
      const raw = extractStreamRaw(res.data);
      let parsed = parseStreamData(raw);

      // Fallback: full workout object may include details.data (getJSON wrapper)
      if (parsed.length <= 4 && workout.details?.data) {
        parsed = parseStreamData(workout.details.data);
      }

      if (parsed.length > 4) {
        setStream(runningDisplayStream(parsed, workout));
      } else {
        setStream([]);
        setStreamError(t('analysis.streamNoData'));
      }
    } catch (err: unknown) {
      setStream([]);
      const msg = err instanceof Error ? err.message : t('analysis.streamLoadFailed');
      setStreamError(msg);
    } finally {
      setStreamLoading(false);
    }
  }, [workout.id, workout.details?.data, workout.fit_sport, workout.source, workout.running_metrics, t]);

  useEffect(() => {
    setStream([]);
    setSelection(null);
    setSidebarHighlightKey(null);
    setActiveLapIndex(null);
    setRangeApiData(null);
    setStreamError(null);
    void loadDetails();
    void loadStream();
  }, [workout.id, loadDetails, loadStream]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const peaks  = details?.peaks ?? workout.details?.peaks ?? {};
  const laps = details?.lap_data ?? [];

  const peakTabs = useMemo(() => [
    { key: 'pw_peak', label: t('pdcChart.tab.power'), data: peaks.pw_peak, unit: 'W', color: `linear-gradient(90deg,${PINK},${PINK_DEEP})`, dec: 0 },
    { key: 'hr_peak', label: t('pdcChart.tab.hr'), data: peaks.hr_peak, unit: 'bpm', color: 'linear-gradient(90deg,#F87171,#EF4444)', dec: 0 },
    { key: 'sd_peak', label: t('pdcChart.tab.speed'), data: peaks.sd_peak, unit: 'km/h', color: `linear-gradient(90deg,${GREEN},${GREEN_D})`, dec: 1 },
    { key: 'cd_peak', label: isRunningWorkout(workout) ? t('runningMetrics.cadence') : t('pdcChart.tab.cadence'), data: isRunningWorkout(workout) && peaks.cd_peak ? Object.fromEntries(Object.entries(peaks.cd_peak).map(([key, value]) => [key, { ...(value as any), value: Number((value as any).value) * 2 }])) : peaks.cd_peak, unit: isRunningWorkout(workout) ? t('runningMetrics.cadenceUnit') : 'rpm', color: 'linear-gradient(90deg,#FCD34D,#F59E0B)', dec: 0 },
  ].map((item) => ({
    ...item,
    hasData: Boolean(item.data && Object.values(item.data).some((entry: any) => entry && Number.isFinite(Number(entry.value)))),
  })), [peaks, t, workout]);

  const availablePeakTabs = useMemo(() => peakTabs.filter((item) => item.hasData), [peakTabs]);
  const peakNavItems = useMemo(() => [
    ...peakTabs.map((item) => ({ value: item.key, label: item.label, disabled: !item.hasData })),
    ...(laps.length ? [{ value: 'laps', label: t('workoutDetail.summary.lapsTab') }] : []),
  ], [laps.length, peakTabs, t]);

  useEffect(() => {
    const availableKeys = [...availablePeakTabs.map((item) => item.key), ...(laps.length ? ['laps'] : [])];
    if (availableKeys.length && !availableKeys.includes(activePeakKey)) setActivePeakKey(availableKeys[0]);
  }, [activePeakKey, availablePeakTabs, laps.length]);

  const activePeak = availablePeakTabs.find((item) => item.key === activePeakKey);
  const lapsActive = activePeakKey === 'laps';

  const currentPdcCurve = useMemo(
    () => normalizePeakCurve(peaks[pdcPeakType] as Record<string, unknown> | undefined),
    [peaks, pdcPeakType],
  );

  const currentPdcLabel = useMemo(
    () => fmtDisplayDate(workout.workout_day ?? ''),
    [workout.workout_day],
  );

  // Fetch comparison PDC curve when date range or peak type changes
  useEffect(() => {
    if (!userId || !compareStart || !compareEnd) return;
    let cancelled = false;
    setPdcLoading(true);
    analyze.getPowerDurationCurve(userId, {
      start_date: compareStart,
      end_date: compareEnd,
      peak_type: pdcPeakType,
      exclude_workout_id: workout.id,
    })
      .then(res => {
        if (cancelled) return;
        const curve = res.data?.data?.curve ?? [];
        setCompareCurve(curve.map(c => ({
          key: c.key,
          interval: c.interval,
          label: c.label,
          value: c.value,
        })));
      })
      .catch(() => { if (!cancelled) setCompareCurve([]); })
      .finally(() => { if (!cancelled) setPdcLoading(false); });
    return () => { cancelled = true; };
  }, [userId, compareStart, compareEnd, pdcPeakType, workout.id]);

  const hrZ = useMemo(() => athleteZoneDistribution(stream, zoneSettings, 'heartRate'), [stream, zoneSettings]);
  const pwZ = useMemo(() => athleteZoneDistribution(stream, zoneSettings, 'power'), [stream, zoneSettings]);
  const totalTime = Number(details?.totalWorkout?.total_time ?? workout.total_time ?? 7200);

  const timelineStream = useMemo(
    () => normalizeWorkoutStreamTimeline(stream, totalTime),
    [stream, totalTime],
  );

  const summaryStats = useMemo(() => analysisSummaryStats({
    ...workout, details: { ...workout.details, ...details },
  } as WorkoutDetail, timelineStream), [workout, details, timelineStream]);

  const selectStreamIndices = useCallback((
    startIdx: number,
    endIdx: number,
    meta?: { highlightKey?: string | null; lapIndex?: number | null },
  ) => {
    if (!timelineStream.length) return;
    const sel = buildChartSelection(timelineStream, totalTime, startIdx, endIdx);
    setSidebarHighlightKey(meta?.highlightKey ?? null);
    setActiveLapIndex(meta?.lapIndex ?? null);
    setSelection(sel);
    fetchRangeForSelection(sel);
  }, [timelineStream, totalTime, fetchRangeForSelection]);

  const handleLapSelect = useCallback((lapIndex: number) => {
    const indices = findStreamIndicesForLap(laps as LapRow[], lapIndex, timelineStream, totalTime);
    if (!indices) return;
    selectStreamIndices(indices.startIdx, indices.endIdx, { lapIndex, highlightKey: null });
  }, [laps, timelineStream, totalTime, selectStreamIndices]);

  const handlePeakSelect = useCallback((entry: Record<string, unknown>, highlightKey: string) => {
    const indices = findStreamIndicesForModalItem(timelineStream, entry);
    if (!indices) return;
    selectStreamIndices(indices.startIdx, indices.endIdx, { highlightKey, lapIndex: null });
  }, [timelineStream, selectStreamIndices]);

  const streamReady = !streamLoading && timelineStream.length > 0;

  const plannedTotalSec = useMemo(() => {
    const fromPlanned = Number(workout.total_time_planned);
    if (fromPlanned > 0) return fromPlanned;
    const flat = buildStructureTimeline(workout.structure ?? null);
    return flat.length ? flat[flat.length - 1].endSec : 0;
  }, [workout.total_time_planned, workout.structure]);

  const structureSegments = useMemo(
    () => buildStructureTimeline(workout.structure ?? null, plannedTotalSec || null),
    [workout.structure, plannedTotalSec],
  );

  // Extract GPS route from stream data (points with valid position_lat/position_long)
  const gpsRoute = useMemo((): [number, number][] => {
    return extractGpsRoute(stream);
  }, [stream]);

  // Extract GPS selection highlight (points within the chart selection range)
  const gpsSelection = useMemo((): [number, number][] | undefined => {
    if (!selection || !gpsRoute.length) return undefined;
    let gpsStart = -1;
    let gpsEnd = -1;
    for (let i = 0, gpsIdx = -1; i < stream.length; i++) {
      if (stream[i].positionLat != null && stream[i].positionLong != null) {
        gpsIdx++;
        if (i >= selection.startIdx && gpsStart === -1) gpsStart = gpsIdx;
        if (i <= selection.endIdx) gpsEnd = gpsIdx;
      }
    }
    if (gpsStart === -1 || gpsEnd === -1 || gpsStart > gpsEnd) return undefined;
    const sel = gpsRoute.slice(gpsStart, gpsEnd + 1);
    return sel.length >= 2 ? sel : undefined;
  }, [stream, gpsRoute, selection]);

  const showPdc = currentPdcCurve.length > 0 || compareCurve.length > 0 || pdcLoading;

  const renderChartPanel = (id: ChartPanelId) => {
    if (id === 'pdc' && !showPdc) return null;

    const common = {
      id,
      label: panelLabels(t)[id],
      flex: id === 'stream',
      isOver: dropOverPanelId === id && dragPanelId !== id,
      isDragging: dragPanelId === id,
      slotAttr: panelSlotAttr,
      onBeginReorder: beginPanelReorder,
    };

    if (id === 'map') {
      return (
        <ChartPanelSlot key="map" {...common}>
          <GpsMap gpsRoute={gpsRoute} gpsSelection={gpsSelection} height={mapHeight} fitKey={String(workout.id)} />
          <MapHeightResizeHandle
            height={mapHeight}
            onResize={setMapHeight}
            onResizeEnd={persistMapHeight}
          />
        </ChartPanelSlot>
      );
    }
    if (id === 'pdc') {
      return (
        <ChartPanelSlot key="pdc" {...common}>
          <div style={{ padding: '4px 10px 10px', flexShrink: 0, width: '100%', boxSizing: 'border-box' }}>
            <PdcCurveChart
              currentCurve={currentPdcCurve}
              compareCurve={compareCurve}
              compareStart={compareStart}
              compareEnd={compareEnd}
              currentLabel={currentPdcLabel}
              cadenceUnit={isRunningWorkout(workout) ? 'spm' : 'rpm'}
              peakType={pdcPeakType}
              onPeakTypeChange={setPdcPeakType}
              onCompareStartChange={setCompareStart}
              onCompareEndChange={setCompareEnd}
              loading={pdcLoading}
              chartHeight={220}
            />
          </div>
        </ChartPanelSlot>
      );
    }
    return (
      <ChartPanelSlot key="stream" {...common}>
        <WorkoutChart running={isRunningWorkout(workout)}
          stream={stream}
          totalTime={totalTime}
          plannedTotalSec={plannedTotalSec}
          selection={selection}
          onSelectionChange={handleSelectionChange}
          streamLoading={streamLoading}
          streamError={streamError}
          structureSegments={structureSegments}
          ftp={ftp}
        />
      </ChartPanelSlot>
    );
  };

  const [shareOpen, setShareOpen] = useState(false);
  const closeShare = useCallback(() => setShareOpen(false), []);
  const handleClose = useCallback(() => { onClose(); }, [onClose]);

  const view = (
    <div
      className="title-bar-no-drag workout-analysis-view"
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: BG, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <TopBar workout={workout} loading={loading} onClose={handleClose} onTitleUpdated={onTitleUpdated} onShare={() => setShareOpen(true)} />
      {shareOpen && <WorkoutShareModal workout={workout} stream={stream} onClose={closeShare} />}

      <div className="workout-analysis-body" style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden', direction: 'ltr' }}>

        {/* ── Charts column: map + stream + PDC ── */}
        <div className="workout-analysis-charts" style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {panelOrder.filter(id => id !== 'pdc').map(id => renderChartPanel(id))}
          {showPdc && (
            <div style={{ flexShrink: 0, width: '100%', borderTop: '1px solid rgba(163,177,198,.22)' }}>
              {renderChartPanel('pdc')}
            </div>
          )}
          <WorkoutPhysiologyPanels snapshot={workout.details?.fit_metrics} mode="analysis" />
          <section className="analysis-zones-panel" aria-label={t('analysis.section.hrZones')}>
            <p className="analysis-zones-note">{t('analysis.zonesCurrent')}</p>
            {([{ zones: hrZ, title: 'hrZones', grads: HR_Z_GRADS, unit: 'bpm' },
              { zones: pwZ, title: 'powerZones', grads: PW_Z_GRADS, unit: 'W' }] as const).map(({ zones, title, grads, unit }) => (
              <section className="analysis-zone-section" key={title} aria-label={t(`analysis.section.${title}`)}>
                <h3>{t(`analysis.section.${title}`)}</h3>
                {zonesLoading || streamLoading ? <p className="analysis-zones-note"><NmLoader size="sm" inline label={t('common.loading')} /></p>
                  : zones && zones.length > 0 ? <ZoneBars zones={zones} grads={grads} unit={unit} />
                  : <p className="analysis-zones-note">{t(zones == null ? 'analysis.zonesUnavailable' : 'analysis.zonesNoSamples')}</p>}
              </section>
            ))}
          </section>
        </div>

        {/* ── Stats panel ── */}
        <div ref={statsPanelRef} className="workout-analysis-stats" style={{ width: 338, flexShrink: 0, background: BG, borderLeft: '1px solid rgba(163,177,198,.22)', overflowY: 'auto', scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}>

          {selection && stream.length > 0 && (
            <SelectionCard
              running={isRunningWorkout(workout)}
              stream={stream}
              selection={selection}
              apiData={rangeApiData}
              apiLoading={rangeApiLoading}
              onClear={() => handleSelectionChange(null)}
            />
          )}
          {selection && stream.length > 0 && comparisonNav && (
            <div style={{ direction: 'ltr', padding: '8px 12px' }}>
              <ActionButton
                type="button"
                onClick={() => {
                  const startSec = selection.timeStartSec ?? stream[selection.startIdx]?.timeSeconds ?? 0;
                  const endSec = selection.timeEndSec ?? stream[selection.endIdx]?.timeSeconds ?? startSec;
                  comparisonNav.openComparison({
                    workoutAId: workout.id,
                    workoutAUserId: userId,
                    segmentA: { startSec, endSec },
                    mode: 'two-workouts',
                  });
                  onClose();
                }}
                style={{ width: '100%' }}
              >{t('analysis.compareSegment')}</ActionButton>
            </div>
          )}

          {/* Whole workout */}
          <RSection>
            <HeroTrio wo={workout} total={details?.totalWorkout} />
          </RSection>

          {/* Whole-workout metrics share one compact tabbed section. */}
          <RSection>
            <div className="analysis-data-card">
                <NmTabNav
                  className="analysis-summary-tabs"
                  items={[{ value: 'performance', label: t('analysis.section.performance') }, { value: 'mam', label: t('analysis.section.mam') }]}
                  value={summaryTab}
                  onChange={setSummaryTab}
                  ariaLabel={t('analysis.section.fullSummary')}
                  idBase="analysis-summary-tab"
                  panelId="analysis-summary-panel"
                />
                <div role="tabpanel" id="analysis-summary-panel" aria-labelledby={`analysis-summary-tab-${summaryTab}`}>
                  {summaryTab === 'performance' ? (isRunningWorkout(workout) ? <RunningMetricsPanel metrics={workout.running_metrics} /> : <StatsGrid wo={workout} total={details?.totalWorkout} />) : <MamTable mma={summaryStats} running={isRunningWorkout(workout)} />}
                </div>
              </div>
          </RSection>

          {/* Peaks */}
          {(availablePeakTabs.length > 0 || laps.length > 0) && (
            <RSection>
              <div className="analysis-data-card">
                <NmTabNav
                  className="analysis-peak-tabs"
                  items={peakNavItems}
                  value={activePeakKey}
                  onChange={setActivePeakKey}
                  ariaLabel={t('analysis.section.peaks')}
                  idBase="analysis-peak-tab"
                />
                {activePeak && (
                  <PeakBars peakData={activePeak.data} title={activePeak.label} unit={activePeak.unit} color={activePeak.color} dec={activePeak.dec} stream={timelineStream} streamReady={streamReady} activeKey={sidebarHighlightKey} onPeakSelect={handlePeakSelect} />
                )}
                {lapsActive && (
                  <LapsTable
                    laps={laps}
                    stream={timelineStream}
                    streamReady={streamReady}
                    workoutDurationSec={totalTime}
                    activeLapIndex={activeLapIndex}
                    onLapSelect={handleLapSelect}
                  />
                )}
              </div>
            </RSection>
          )}

        </div>
      </div>
    </div>
  );

  return createPortal(view, document.body);
}
